import React, { useState, useCallback, useEffect } from 'react';
import axios from 'axios';
import { format, addDays, startOfWeek, isAfter, isBefore, startOfDay } from 'date-fns';
import { formatInTimeZone } from 'date-fns-tz';
import { DayPicker } from 'react-day-picker';
import 'react-day-picker/dist/style.css';
import { 
  Building2, User, Mail, Phone, MapPin, Calendar, Clock, 
  Users, FileText, ChevronRight, ArrowLeft, Edit2, Save, X,
  Loader2, CheckCircle, AlertCircle, Plus, Trash2, ChevronDown,
  ChevronUp, ExternalLink, CalendarPlus, AlertTriangle, DollarSign, Tag,
  Lock, Eye, EyeOff, Key, Shield, UserCog, CreditCard, Check, RefreshCw
} from 'lucide-react';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import AddressAutocomplete from '@/components/AddressAutocomplete';
import { formatPhoneNumber } from '../utils/formatters';
// Inline card vaulting using Stripe SetupIntent
import StripeCardVaultForm from '@/components/StripeCardVaultForm';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Inline card vaulting is now handled by StripeCardVaultForm component

const ClientPortal = () => {
  // Auth state
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [loading, setLoading] = useState(true); // Start with loading=true to check stored session
  const [showPassword, setShowPassword] = useState(false);
  
  // Password change state
  const [mustChangePassword, setMustChangePassword] = useState(false);
  const [showChangePassword, setShowChangePassword] = useState(false);
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [changingPassword, setChangingPassword] = useState(false);
  
  // Permissions for team members
  const [permissions, setPermissions] = useState(null); // null = primary (all access)
  
  // Client data
  const [client, setClient] = useState(null);
  const [isPrimary, setIsPrimary] = useState(true); // Whether current user is primary contact
  const [contactIndex, setContactIndex] = useState(null); // For secondary contacts
  const [contactId, setContactId] = useState(null); // For secondary contacts
  const [primaryContact, setPrimaryContact] = useState(null); // Primary contact info for secondary users
  const [bookings, setBookings] = useState([]);
  const [invoices, setInvoices] = useState([]);
  
  // Edit states
  const [editingInfo, setEditingInfo] = useState(false);
  const [editingLocations, setEditingLocations] = useState(false);
  const [infoForm, setInfoForm] = useState({});
  const [locationsForm, setLocationsForm] = useState([]);
  const [savingInfo, setSavingInfo] = useState(false);
  const [savingLocations, setSavingLocations] = useState(false);
  
  // Email change state
  const [editingEmail, setEditingEmail] = useState(false);
  const [newEmail, setNewEmail] = useState('');
  const [showEmailConfirm, setShowEmailConfirm] = useState(false);
  const [savingEmail, setSavingEmail] = useState(false);
  
  // Expanded classes state
  const [expandedBookings, setExpandedBookings] = useState({});
  const [showPastClasses, setShowPastClasses] = useState(false); // Collapsed by default
  
  // Booking request state
  const [classTypes, setClassTypes] = useState([]);
  const [selectedClassType, setSelectedClassType] = useState(null);
  const [selectedDate, setSelectedDate] = useState(undefined);
  const [availability, setAvailability] = useState(null);
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [bookingStudents, setBookingStudents] = useState([{ 
    id: Date.now(), 
    firstName: '', 
    lastName: '', 
    email: '', 
    phone: '',
    ahaEcardCode: '',
    ahaVerified: false,
    ahaVerificationStatus: null,
    errors: {}
  }]);
  const [bookingAddress, setBookingAddress] = useState({ street: '', city: '', state: 'NV', zip: '' });
  const [bookingLocationName, setBookingLocationName] = useState('');
  const [bookingNotes, setBookingNotes] = useState('');
  const [customBookingCode, setCustomBookingCode] = useState('');
  const [submittingBooking, setSubmittingBooking] = useState(false);
  const [bookingSuccess, setBookingSuccess] = useState(null);
  const [loadingAvailability, setLoadingAvailability] = useState(false);
  
  // Terms and pricing state
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [livePricing, setLivePricing] = useState(null);
  const [loadingPricing, setLoadingPricing] = useState(false);

  // Team contacts state (primary users only)
  const [showAddContact, setShowAddContact] = useState(false);
  const [editingContact, setEditingContact] = useState(null);
  const [contactForm, setContactForm] = useState({ first_name: '', last_name: '', email: '', phone: '', title: '', temporary_password: '' });
  const [savingContact, setSavingContact] = useState(false);

  // Team member password reset state (for primary users)
  const [resetPasswordContact, setResetPasswordContact] = useState(null);
  const [resetTempPassword, setResetTempPassword] = useState('');
  const [resettingPassword, setResettingPassword] = useState(false);
  
  // Team member permissions state (for primary users)
  const [editPermissionsContact, setEditPermissionsContact] = useState(null);
  const [editPermissionsIndex, setEditPermissionsIndex] = useState(null);
  const [tempPermissions, setTempPermissions] = useState({});
  const [savingPermissions, setSavingPermissions] = useState(false);
  
  // Swap primary state
  const [showSwapPrimary, setShowSwapPrimary] = useState(false);
  const [swappingPrimary, setSwappingPrimary] = useState(false);
  const [swapTargetIndex, setSwapTargetIndex] = useState(null);

  // Add student to class state
  const [addStudentBooking, setAddStudentBooking] = useState(null); // The booking to add student to
  const [addStudentForm, setAddStudentForm] = useState({ first_name: '', last_name: '', email: '', phone: '', aha_ecard_code: '' });
  const [addingStudent, setAddingStudent] = useState(false);
  const [addStudentResult, setAddStudentResult] = useState(null); // Result after attempting to add
  const [ahaVerificationStatus, setAhaVerificationStatus] = useState(null); // AHA verification result
  const [verifyingAha, setVerifyingAha] = useState(false); // Loading state for verification
  const [manualVerifyConfirmed, setManualVerifyConfirmed] = useState(false); // Manual verification checkbox
  const [removingStudent, setRemovingStudent] = useState(null); // Track which student is being removed {bookingId, studentIndex}
  
  // AHA Mismatch Warning Modal state
  const [ahaMismatchModal, setAhaMismatchModal] = useState({
    show: false,
    context: null, // 'addStudent' or 'booking'
    studentId: null,
    enteredName: '',
    ahaName: '',
    verificationResult: null
  });
  
  // Payment Method (Vault) state
  const [paymentMethod, setPaymentMethod] = useState(null); // {has_payment_method, card_last_four, card_brand, etc}
  const [paymentMethodLoading, setPaymentMethodLoading] = useState(false);
  const [showAddPaymentMethod, setShowAddPaymentMethod] = useState(false);
  const [addingPaymentMethod, setAddingPaymentMethod] = useState(false);
  const [deletingPaymentMethod, setDeletingPaymentMethod] = useState(false);
  const [togglingAutoCharge, setTogglingAutoCharge] = useState(false);
  const [vaultEnabled, setVaultEnabled] = useState(false); // Whether vault/billing is enabled by admin
  const [stripeClientId, setStripeClientId] = useState(null); // Stripe client ID for SDK
  const [setupTokenId, setSetupTokenId] = useState(null); // Current setup token for vaulting
  const [showCardFieldsDialog, setShowCardFieldsDialog] = useState(false); // Show card entry dialog
  const [activeTab, setActiveTab] = useState('book'); // Track active tab for persistence
  
  // Default permissions for team members
  const DEFAULT_PERMISSIONS = {
    classes: true,
    book_class: true,
    class_pricing: true,
    invoices: false,
    saved_locations: true,
    team_contacts: false,
    make_payments: false  // Permission to use saved card to pay for classes
  };
  
  // Maximum upcoming classes allowed from client portal
  const MAX_UPCOMING_CLASSES = 3;
  
  // Calculate upcoming bookings count
  const upcomingBookingsCount = bookings.filter(b => !b.is_past).length;
  const hasReachedMaxBookings = upcomingBookingsCount >= MAX_UPCOMING_CLASSES;
  
  // Password validation
  const validatePassword = (pwd) => {
    const errors = [];
    if (pwd.length < 8) errors.push('At least 8 characters');
    if (!/[A-Z]/.test(pwd)) errors.push('One uppercase letter');
    if (!/[a-z]/.test(pwd)) errors.push('One lowercase letter');
    if (!/[0-9]/.test(pwd)) errors.push('One number');
    if (!/[!@#$%^&*()_+\-=\[\]{}|;:,.<>?]/.test(pwd)) errors.push('One special character');
    return errors;
  };

  // Toggle booking expansion
  const toggleBookingExpanded = (bookingId) => {
    setExpandedBookings(prev => ({
      ...prev,
      [bookingId]: !prev[bookingId]
    }));
  };

  // Login with email and password
  const handleLogin = async (e) => {
    e.preventDefault();
    if (!email.trim()) {
      toast.error('Please enter your email address');
      return;
    }
    if (!password) {
      toast.error('Please enter your password');
      return;
    }
    
    setLoading(true);
    try {
      const response = await axios.post(`${API}/client-portal/login`, { 
        email: email.trim(),
        password: password
      });
      
      if (response.data.success) {
        setClient(response.data.client);
        setIsAuthenticated(true);
        setIsPrimary(response.data.is_primary);
        setContactIndex(response.data.contact_index ?? null);
        setPermissions(response.data.permissions || null);
        
        if (response.data.contact) {
          setPrimaryContact({
            name: response.data.client.contact_name,
            email: response.data.client.email
          });
        }
        
        // Store session in localStorage for page refresh persistence
        const sessionData = {
          email: email.trim(),
          clientId: response.data.client.id,
          client: response.data.client,
          isPrimary: response.data.is_primary,
          contactIndex: response.data.contact_index ?? null,
          contactId: response.data.contact?.id ?? null,
          permissions: response.data.permissions || null,
          primaryContact: response.data.contact ? {
            name: response.data.client.contact_name,
            email: response.data.client.email
          } : null
        };
        localStorage.setItem('clientPortalSession', JSON.stringify(sessionData));
        
        // Check if password change is required
        if (response.data.must_change_password) {
          setMustChangePassword(true);
          setShowChangePassword(true);
          toast.info('Please change your password to continue');
        } else {
          // Fetch bookings and invoices
          await fetchClientData(response.data.client.id, email.trim());
          
          const welcomeName = response.data.is_primary 
            ? (response.data.client.contact_name || 'Client')
            : (response.data.contact?.first_name || 'Team Member');
          
          if (!response.data.is_primary) {
            toast.success(`Welcome back, ${welcomeName}! You are logged in as a team member.`);
          } else {
            toast.success(`Welcome back, ${welcomeName}!`);
          }
        }
      }
    } catch (error) {
      if (error.response?.status === 401) {
        const msg = error.response?.data?.detail || 'Invalid email or password';
        toast.error(msg);
      } else {
        toast.error('Error logging in. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };
  
  // Handle password change
  const handleChangePassword = async () => {
    if (!newPassword || !confirmPassword) {
      toast.error('Please fill in all password fields');
      return;
    }
    
    if (newPassword !== confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }
    
    const errors = validatePassword(newPassword);
    if (errors.length > 0) {
      toast.error(`Password requirements: ${errors.join(', ')}`);
      return;
    }
    
    // For forced password change, current password is the one they just logged in with
    const currPwd = mustChangePassword ? password : currentPassword;
    
    if (!currPwd) {
      toast.error('Please enter your current password');
      return;
    }
    
    setChangingPassword(true);
    try {
      await axios.post(
        `${API}/client-portal/${client.id}/change-password?email=${encodeURIComponent(email)}`,
        {
          current_password: currPwd,
          new_password: newPassword
        }
      );
      
      toast.success('Password changed successfully!');
      setShowChangePassword(false);
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
      setPassword(newPassword); // Update stored password
      
      // If this was a forced change, now fetch data
      if (mustChangePassword) {
        setMustChangePassword(false);
        await fetchClientData(client.id, email.trim());
        toast.success(`Welcome! You can now access your portal.`);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error changing password');
    } finally {
      setChangingPassword(false);
    }
  };
  
  // Reset team member password (primary only)
  const handleResetTeamPassword = async () => {
    if (!resetTempPassword) {
      toast.error('Please enter a temporary password');
      return;
    }
    
    const errors = validatePassword(resetTempPassword);
    if (errors.length > 0) {
      toast.error(`Password requirements: ${errors.join(', ')}`);
      return;
    }
    
    setResettingPassword(true);
    try {
      await axios.post(
        `${API}/client-portal/${client.id}/reset-team-password?email=${encodeURIComponent(email)}`,
        {
          contact_index: resetPasswordContact.index,
          temporary_password: resetTempPassword
        }
      );
      
      toast.success(`Password reset for ${resetPasswordContact.first_name}. They will be prompted to change it on login.`);
      setResetPasswordContact(null);
      setResetTempPassword('');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error resetting password');
    } finally {
      setResettingPassword(false);
    }
  };
  
  // Save team member permissions (primary only)
  const handleSavePermissions = async () => {
    setSavingPermissions(true);
    try {
      await axios.put(
        `${API}/client-portal/${client.id}/team-permissions?email=${encodeURIComponent(email)}`,
        {
          contact_index: editPermissionsIndex,
          permissions: tempPermissions
        }
      );
      
      // Update local state
      setClient(prev => ({
        ...prev,
        contacts: prev.contacts.map((c, i) => 
          i === editPermissionsIndex ? { ...c, permissions: tempPermissions } : c
        )
      }));
      
      toast.success('Permissions updated!');
      setEditPermissionsContact(null);
      setEditPermissionsIndex(null);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error saving permissions');
    } finally {
      setSavingPermissions(false);
    }
  };
  
  // Swap primary contact (primary only)
  const handleSwapPrimary = async () => {
    if (swapTargetIndex === null) {
      toast.error('Please select a team member');
      return;
    }
    
    setSwappingPrimary(true);
    try {
      const response = await axios.post(
        `${API}/client-portal/${client.id}/swap-primary?email=${encodeURIComponent(email)}`,
        { contact_index: swapTargetIndex }
      );
      
      toast.success(response.data.message);
      
      // Log out and prompt to log back in with new role
      setIsAuthenticated(false);
      setClient(null);
      setEmail('');
      setPassword('');
      setShowSwapPrimary(false);
      setSwapTargetIndex(null);
      toast.info('Please log in again with your credentials.');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error swapping primary contact');
    } finally {
      setSwappingPrimary(false);
    }
  };
  
  // Check if user has permission for a tab
  const hasPermission = (tab) => {
    if (isPrimary || !permissions) return true;
    return permissions[tab] !== false;
  };

  // Look up client by email (legacy - now uses password login)
  const handleLookup = async (e) => {
    e.preventDefault();
    // Redirect to password login
    handleLogin(e);
  };

  // Fetch bookings and invoices
  const fetchClientData = async (clientId, clientEmail) => {
    try {
      const [bookingsRes, invoicesRes] = await Promise.all([
        axios.get(`${API}/client-portal/${clientId}/bookings?email=${encodeURIComponent(clientEmail)}`),
        axios.get(`${API}/client-portal/${clientId}/invoices?email=${encodeURIComponent(clientEmail)}`)
      ]);
      
      setBookings(bookingsRes.data.bookings || []);
      setInvoices(invoicesRes.data.invoices || []);
    } catch (error) {
      console.error('Error fetching client data:', error);
    }
  };

  // Fetch vault enabled setting
  const fetchVaultEnabled = async () => {
    try {
      const response = await axios.get(`${API}/client-portal/vault-enabled`);
      setVaultEnabled(response.data.enabled);
    } catch (error) {
      console.error('Error fetching vault enabled:', error);
      setVaultEnabled(false);
    }
  };

  // Fetch saved payment method
  const fetchPaymentMethod = async (clientId) => {
    setPaymentMethodLoading(true);
    try {
      const response = await axios.get(`${API}/client-portal/vault/payment-method/${clientId}`);
      setPaymentMethod(response.data);
    } catch (error) {
      console.error('Error fetching payment method:', error);
      setPaymentMethod({ has_payment_method: false });
    } finally {
      setPaymentMethodLoading(false);
    }
  };

  // Start adding a payment method - open inline card form dialog
  const handleAddPaymentMethod = async () => {
    if (!client?.id) return;
    
    // Open the inline card fields dialog
    setShowCardFieldsDialog(true);
  };

  // Handle successful card save
  const handleCardSaveSuccess = async (data) => {
    setShowCardFieldsDialog(false);
    setSetupTokenId(null);
    toast.success(`Card saved: ${data.card_brand || data.cardBrand} ending in ${data.card_last_four || data.cardLastFour}`);
    await fetchPaymentMethod(client.id);
  };

  // Handle card save error
  const handleCardSaveError = (error) => {
    console.error('Card save error:', error);
    toast.error(error.message || 'Failed to save card. Please try again.');
  };

  // Complete payment method setup (after Stripe redirect)
  const completePaymentMethodSetup = async (setupTokenId, clientId) => {
    try {
      const response = await axios.post(
        `${API}/client-portal/vault/save-payment-method?setup_token_id=${setupTokenId}&recurring_client_id=${clientId}`
      );
      
      if (response.data.success) {
        toast.success(`Card saved: ${response.data.card_brand} ending in ${response.data.card_last_four}`);
        await fetchPaymentMethod(clientId);
        // Clean up URL
        window.history.replaceState({}, '', window.location.pathname);
        sessionStorage.removeItem('vault_setup_token');
      } else {
        toast.error(response.data.error || 'Failed to save payment method');
      }
    } catch (error) {
      console.error('Error completing payment setup:', error);
      toast.error('Failed to save payment method');
    }
  };

  // Delete payment method
  const handleDeletePaymentMethod = async () => {
    if (!client?.id) return;
    if (!window.confirm('Are you sure you want to remove this payment method?')) return;
    
    setDeletingPaymentMethod(true);
    try {
      const response = await axios.delete(`${API}/client-portal/vault/payment-method/${client.id}`);
      if (response.data.success) {
        toast.success('Payment method removed');
        setPaymentMethod({ has_payment_method: false });
      }
    } catch (error) {
      console.error('Error deleting payment method:', error);
      toast.error('Failed to remove payment method');
    } finally {
      setDeletingPaymentMethod(false);
    }
  };

  // Toggle auto-charge preference
  const handleToggleAutoCharge = async () => {
    if (!client?.id || !paymentMethod) return;
    
    const newValue = !paymentMethod.auto_charge_enabled;
    setTogglingAutoCharge(true);
    try {
      const response = await axios.post(
        `${API}/client-portal/vault/toggle-auto-charge/${client.id}?enabled=${newValue}`
      );
      if (response.data.success) {
        setPaymentMethod({ ...paymentMethod, auto_charge_enabled: newValue });
        toast.success(newValue ? 'Auto-charge enabled' : 'Auto-charge disabled - you will receive invoices instead');
      }
    } catch (error) {
      console.error('Error toggling auto-charge:', error);
      toast.error('Failed to update preference');
    } finally {
      setTogglingAutoCharge(false);
    }
  };

  // Pay for a booking using saved card
  // Save contact info
  const handleSaveInfo = async () => {
    setSavingInfo(true);
    try {
      const response = await axios.put(
        `${API}/client-portal/${client.id}/update?email=${encodeURIComponent(email)}`,
        infoForm
      );
      
      if (response.data.success) {
        setClient(response.data.client);
        setEditingInfo(false);
        toast.success('Contact information updated');
      }
    } catch (error) {
      toast.error('Error updating information');
    } finally {
      setSavingInfo(false);
    }
  };

  // Save locations
  const handleSaveLocations = async () => {
    // Validate locations
    const validLocations = locationsForm.filter(loc => loc.name && loc.address?.street);
    
    setSavingLocations(true);
    try {
      const response = await axios.put(
        `${API}/client-portal/${client.id}/locations?email=${encodeURIComponent(email)}`,
        validLocations
      );
      
      if (response.data.success) {
        setClient({ ...client, locations: response.data.locations });
        setLocationsForm(response.data.locations);
        setEditingLocations(false);
        toast.success('Locations updated');
      }
    } catch (error) {
      toast.error('Error updating locations');
    } finally {
      setSavingLocations(false);
    }
  };

  // Handle email change
  const handleEmailChange = async () => {
    if (!newEmail.trim()) {
      toast.error('Please enter a new email address');
      return;
    }
    
    setSavingEmail(true);
    try {
      const response = await axios.put(
        `${API}/client-portal/${client.id}/update-email?current_email=${encodeURIComponent(email)}&new_email=${encodeURIComponent(newEmail.trim())}`
      );
      
      if (response.data.success) {
        setEmail(response.data.new_email);
        setClient({ ...client, email: response.data.new_email });
        setEditingEmail(false);
        setShowEmailConfirm(false);
        setNewEmail('');
        toast.success('Email updated successfully. Use your new email to access the portal.');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error updating email');
    } finally {
      setSavingEmail(false);
    }
  };

  // Format date for display
  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    try {
      const date = new Date(dateStr);
      return date.toLocaleDateString('en-US', { 
        weekday: 'short', 
        month: 'short', 
        day: 'numeric', 
        year: 'numeric' 
      });
    } catch {
      return dateStr;
    }
  };

  const formatTime = (dateStr) => {
    if (!dateStr) return '';
    try {
      const date = new Date(dateStr);
      return date.toLocaleTimeString('en-US', { 
        hour: 'numeric', 
        minute: '2-digit',
        hour12: true
      });
    } catch {
      return '';
    }
  };

  // Logout
  const handleLogout = () => {
    // Clear localStorage session
    localStorage.removeItem('clientPortalSession');
    localStorage.removeItem('clientPortalActiveTab');
    
    setIsAuthenticated(false);
    setClient(null);
    setIsPrimary(true);
    setContactId(null);
    setContactIndex(null);
    setPermissions(null);
    setPrimaryContact(null);
    setEmail('');
    setPassword('');
    setBookings([]);
    setInvoices([]);
    setBookingSuccess(null);
    setActiveTab('classes');
  };

  // Add a new team contact (primary users only)
  const handleAddContact = async () => {
    if (!contactForm.first_name || !contactForm.last_name || !contactForm.email) {
      toast.error('First name, last name, and email are required');
      return;
    }
    
    if (!contactForm.temporary_password) {
      toast.error('A temporary password is required');
      return;
    }
    
    const pwdErrors = validatePassword(contactForm.temporary_password);
    if (pwdErrors.length > 0) {
      toast.error(`Password requirements: ${pwdErrors.join(', ')}`);
      return;
    }
    
    setSavingContact(true);
    try {
      const response = await axios.post(
        `${API}/client-portal/${client.id}/contacts?email=${encodeURIComponent(email)}`,
        contactForm
      );
      
      if (response.data.success) {
        // Add to local state
        setClient({
          ...client,
          contacts: [...(client.contacts || []), response.data.contact]
        });
        setShowAddContact(false);
        setContactForm({ first_name: '', last_name: '', email: '', phone: '', title: '', temporary_password: '' });
        toast.success('Team member added successfully. They can now log in with the temporary password.');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add contact');
    } finally {
      setSavingContact(false);
    }
  };

  // Update a team contact
  const handleUpdateContact = async (contactId) => {
    if (!contactForm.first_name || !contactForm.last_name || !contactForm.email) {
      toast.error('First name, last name, and email are required');
      return;
    }
    
    setSavingContact(true);
    try {
      const response = await axios.put(
        `${API}/client-portal/${client.id}/contacts/${contactId}?email=${encodeURIComponent(email)}`,
        contactForm
      );
      
      if (response.data.success) {
        // Update local state
        setClient({
          ...client,
          contacts: (client.contacts || []).map(c => 
            c.id === contactId ? response.data.contact : c
          )
        });
        setEditingContact(null);
        setContactForm({ first_name: '', last_name: '', email: '', phone: '', title: '' });
        toast.success('Contact updated successfully');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update contact');
    } finally {
      setSavingContact(false);
    }
  };

  // Delete a team contact (primary users only)
  const handleDeleteContact = async (contactId) => {
    if (!confirm('Are you sure you want to remove this team member? They will no longer be able to access the client portal.')) {
      return;
    }
    
    try {
      await axios.delete(
        `${API}/client-portal/${client.id}/contacts/${contactId}?email=${encodeURIComponent(email)}`
      );
      
      // Remove from local state
      setClient({
        ...client,
        contacts: (client.contacts || []).filter(c => c.id !== contactId)
      });
      toast.success('Team member removed');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to remove contact');
    }
  };

  // Check for stored session on mount (page refresh)
  useEffect(() => {
    const checkStoredSession = async () => {
      try {
        const storedSession = localStorage.getItem('clientPortalSession');
        if (storedSession) {
          const session = JSON.parse(storedSession);
          // Verify the session is still valid by fetching client data
          if (session.email && session.clientId) {
            const response = await axios.get(
              `${API}/client-portal/${session.clientId}/bookings?email=${encodeURIComponent(session.email)}`
            );
            
            if (response.data) {
              // Session is valid, restore state
              setEmail(session.email);
              setClient(session.client);
              setIsAuthenticated(true);
              setIsPrimary(session.isPrimary ?? true);
              setContactIndex(session.contactIndex ?? null);
              setContactId(session.contactId ?? null);
              setPermissions(session.permissions ?? null);
              if (session.primaryContact) {
                setPrimaryContact(session.primaryContact);
              }
              
              // Fetch fresh data
              setBookings(response.data.bookings || []);
              setInvoices(response.data.invoices || []);
              
              // Restore active tab if stored
              const storedTab = localStorage.getItem('clientPortalActiveTab');
              if (storedTab) {
                setActiveTab(storedTab);
              }
            }
          }
        }
      } catch (error) {
        // Session expired or invalid, clear storage
        console.log('Session expired or invalid, clearing stored session');
        localStorage.removeItem('clientPortalSession');
        localStorage.removeItem('clientPortalActiveTab');
      } finally {
        setLoading(false);
      }
    };
    
    checkStoredSession();
  }, []);

  // Fetch class types for booking request - only show client's preferred classes
  useEffect(() => {
    if (isAuthenticated && client) {
      const fetchClassTypes = async () => {
        try {
          // Use class-types-with-prices to get WooCommerce cached prices
          const response = await axios.get(`${API}/class-types-with-prices`);
          const allClassTypes = Array.isArray(response.data) ? response.data : response.data.class_types || [];
          
          // Filter to only show client's preferred classes
          const preferredClassIds = (client.preferred_classes || []).map(pc => pc.class_id);
          if (preferredClassIds.length > 0) {
            const filteredTypes = allClassTypes.filter(ct => preferredClassIds.includes(ct.id));
            setClassTypes(filteredTypes);
          } else {
            // If no preferred classes, show all (fallback)
            setClassTypes(allClassTypes);
          }
        } catch (error) {
          console.error('Error fetching class types:', error);
        }
      };
      fetchClassTypes();
      
      // Fetch vault enabled setting and payment method info
      fetchVaultEnabled();
      fetchPaymentMethod(client.id);
    }
  }, [isAuthenticated, client]);

  // Handle Stripe vault redirect callback
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const vaultSetup = urlParams.get('vault_setup');
    const clientIdParam = urlParams.get('client_id');
    const sessionId = urlParams.get('session_id');
    
    if (vaultSetup === 'success' && clientIdParam && sessionId) {
      // Complete the card setup using the Stripe session ID
      const completeSetup = async () => {
        try {
          const response = await axios.post(`${API}/client-portal/${clientIdParam}/complete-card-setup?session_id=${sessionId}`);
          if (response.data.success) {
            toast.success(`Card ending in ${response.data.card_last_four} saved successfully!`);
            // Refresh payment method info
            if (client?.id) {
              fetchPaymentMethod(client.id);
            }
          }
        } catch (error) {
          console.error('Error completing card setup:', error);
          toast.error('Failed to save card. Please try again.');
        }
        // Clean up URL
        window.history.replaceState({}, '', window.location.pathname);
      };
      completeSetup();
    } else if (vaultSetup === 'cancelled') {
      toast.info('Payment method setup was cancelled');
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, [client]);

  // Fetch availability when date changes - use same endpoint as main booking page
  useEffect(() => {
    if (!selectedClassType || !selectedDate) {
      setAvailability(null);
      return;
    }
    
    const fetchAvailability = async () => {
      setLoadingAvailability(true);
      try {
        // Format date as YYYY-MM-DD to match main booking page
        const year = selectedDate.getFullYear();
        const month = String(selectedDate.getMonth() + 1).padStart(2, '0');
        const day = String(selectedDate.getDate()).padStart(2, '0');
        const dateStr = `${year}-${month}-${day}`;
        
        // Use the same endpoint as main booking page
        const response = await axios.get(`${API}/calendar/availability`, {
          params: {
            date: dateStr,
            class_type_id: selectedClassType.id
          }
        });
        setAvailability(response.data);
      } catch (error) {
        console.error('Error fetching availability:', error);
        setAvailability(null);
      } finally {
        setLoadingAvailability(false);
      }
    };
    fetchAvailability();
  }, [selectedClassType, selectedDate]);

  // Calculate live pricing when students or class type changes
  useEffect(() => {
    if (!selectedClassType || !client) {
      setLivePricing(null);
      return;
    }
    
    const calculatePricing = async () => {
      setLoadingPricing(true);
      try {
        // Get custom price for this class if available
        const preferredClass = (client.preferred_classes || []).find(
          pc => pc.class_id === selectedClassType.id
        );
        const customPrice = preferredClass?.custom_price;
        
        // Calculate student count (only count students with complete info)
        const validStudents = bookingStudents.filter(s => 
          s.firstName.trim() && s.lastName.trim() && s.email.trim() && s.phone.trim()
        );
        const studentCount = validStudents.length; // Can be 0
        
        // Get base price - custom or default
        let basePrice = customPrice;
        if (basePrice === null || basePrice === undefined) {
          // Use a default price estimate (actual will be calculated on backend)
          basePrice = 125; // Default estimate
        }
        
        const studentTotal = basePrice * studentCount;
        
        // Check if address is outside service area (Reno/Sparks)
        let travelFee = 0;
        if (bookingAddress.city && 
            !['reno', 'sparks'].includes(bookingAddress.city.toLowerCase())) {
          travelFee = 75; // Estimate - actual calculated on backend
        }
        
        const total = studentTotal + travelFee;
        
        setLivePricing({
          basePrice,
          customPrice,
          studentCount,
          studentTotal,
          travelFee,
          total,
          isEstimate: customPrice === null || customPrice === undefined
        });
      } catch (error) {
        console.error('Error calculating pricing:', error);
      } finally {
        setLoadingPricing(false);
      }
    };
    
    calculatePricing();
  }, [selectedClassType, bookingStudents, bookingAddress.city, client]);

  // Add student to booking request
  const addBookingStudent = () => {
    setBookingStudents([...bookingStudents, { 
      id: Date.now(), 
      firstName: '', 
      lastName: '', 
      email: '', 
      phone: '',
      ahaEcardCode: '',
      ahaVerified: false,
      ahaVerificationStatus: null,
      errors: {}
    }]);
  };

  // Remove student from booking request
  const removeBookingStudent = (id) => {
    if (bookingStudents.length > 1) {
      setBookingStudents(bookingStudents.filter(s => s.id !== id));
    }
  };

  // Update student in booking request
  const updateBookingStudent = (id, field, value) => {
    setBookingStudents(bookingStudents.map(s => 
      s.id === id ? { ...s, [field]: value, errors: { ...s.errors, [field]: '' } } : s
    ));
  };

  // Verify AHA eCard for a specific student in booking form
  const verifyStudentAhaEcard = async (studentId) => {
    const student = bookingStudents.find(s => s.id === studentId);
    if (!student) return;

    if (!student.firstName?.trim() || !student.lastName?.trim() || !student.ahaEcardCode?.trim()) {
      toast.error('Please enter student name and eCard code');
      return;
    }

    // Update student to show verifying state
    setBookingStudents(bookingStudents.map(s => 
      s.id === studentId ? { ...s, ahaVerificationStatus: { verifying: true } } : s
    ));

    try {
      const response = await axios.post(`${API}/verify-aha-ecard`, {
        first_name: student.firstName.trim(),
        last_name: student.lastName.trim(),
        ecard_code: student.ahaEcardCode.trim()
      });

      const verificationResult = response.data;
      const status = verificationResult.match_status || '';
      const isValid = verificationResult.verified === true;
      const isExactMatch = verificationResult.is_exact_match === true;

      // Check if it's a partial match (verified but not exact)
      if (isValid && !isExactMatch) {
        const enteredName = `${student.firstName} ${student.lastName}`;
        const ahaName = verificationResult.card_holder_name || verificationResult.aha_returned_name;
        
        // Stop verifying state
        setBookingStudents(bookingStudents.map(s => 
          s.id === studentId ? { ...s, ahaVerificationStatus: null } : s
        ));
        
        // Show custom modal
        setAhaMismatchModal({
          show: true,
          context: 'booking',
          studentId: studentId,
          enteredName: enteredName,
          ahaName: ahaName,
          verificationResult: verificationResult
        });
        return;
      }

      // Exact match or failed verification
      setBookingStudents(bookingStudents.map(s => 
        s.id === studentId ? { 
          ...s, 
          ahaVerified: isValid,
          ahaVerificationStatus: verificationResult
        } : s
      ));

      if (isValid) {
        if (isExactMatch) {
          toast.success('✓ AHA eCard verified - Exact Match!');
        } else {
          toast.success('AHA eCard verified successfully!');
        }
      } else if (status === 'MANUAL VERIFICATION REQUIRED' || status === 'AHA UNCLAIMED') {
        toast.warning('Manual verification required - please confirm the eCard matches');
      } else {
        toast.error('Verification failed - please check the eCard code');
      }
    } catch (error) {
      setBookingStudents(bookingStudents.map(s => 
        s.id === studentId ? { 
          ...s, 
          ahaVerified: false,
          ahaVerificationStatus: { error: error.response?.data?.detail || 'Verification failed' }
        } : s
      ));
      toast.error(error.response?.data?.detail || 'Failed to verify eCard');
    }
  };

  // Validate booking form
  const validateBookingForm = () => {
    let isValid = true;
    
    if (!selectedClassType) {
      toast.error('Please select a class type');
      return false;
    }
    
    if (!selectedSlot) {
      toast.error('Please select a date and time');
      return false;
    }
    
    if (!bookingAddress.street || !bookingAddress.city || !bookingAddress.zip) {
      toast.error('Please enter a complete address');
      return false;
    }
    
    // Only validate students that have been partially filled out
    // Students are optional - can submit with no students
    const studentsWithData = bookingStudents.filter(s => 
      s.firstName.trim() || s.lastName.trim() || s.email.trim() || s.phone.trim()
    );
    
    const updatedStudents = bookingStudents.map(student => {
      const hasAnyData = student.firstName.trim() || student.lastName.trim() || 
                         student.email.trim() || student.phone.trim();
      
      // Only validate if student has some data entered
      if (!hasAnyData) {
        return { ...student, errors: {} };
      }
      
      const errors = {};
      if (!student.firstName.trim()) { errors.firstName = 'Required'; isValid = false; }
      if (!student.lastName.trim()) { errors.lastName = 'Required'; isValid = false; }
      if (!student.email.trim()) { errors.email = 'Required'; isValid = false; }
      if (!student.phone.trim()) { errors.phone = 'Required'; isValid = false; }
      return { ...student, errors };
    });
    
    setBookingStudents(updatedStudents);
    return isValid;
  };

  // Submit booking request
  const handleSubmitBookingRequest = async () => {
    if (!validateBookingForm()) return;
    
    // For renewal classes, check if students have been verified
    if (isSelectedClassRenewal()) {
      const studentsWithData = bookingStudents.filter(s => s.firstName.trim() && s.lastName.trim());
      const unverifiedStudents = studentsWithData.filter(s => !s.ahaVerified && !s.ahaVerificationStatus?.match_status?.includes('MATCH'));
      
      if (studentsWithData.length > 0 && unverifiedStudents.length > 0) {
        toast.error('Please verify all student AHA eCard codes before submitting');
        return;
      }
    }
    
    setSubmittingBooking(true);
    try {
      // Only include students that have complete data
      const validStudents = bookingStudents
        .filter(s => s.firstName.trim() && s.lastName.trim() && s.email.trim() && s.phone.trim())
        .map(s => {
          const studentData = {
            first_name: s.firstName.trim(),
            last_name: s.lastName.trim(),
            email: s.email.trim(),
            phone: s.phone.trim()
          };
          
          // Include AHA verification data for renewal classes
          if (isSelectedClassRenewal() && s.ahaEcardCode) {
            studentData.aha_ecard = {
              ecard_code: s.ahaEcardCode.trim(),
              verification_name: s.ahaVerificationStatus?.card_holder_name || s.ahaVerificationStatus?.aha_returned_name || '',
              verification_status: s.ahaVerificationStatus?.match_status || '',
              expiration_date: s.ahaVerificationStatus?.expiration_date || '',
              manually_verified: s.ahaVerified && !s.ahaVerificationStatus?.match_status?.includes('MATCH')
            };
            studentData.aha_verified = s.ahaVerified || (s.ahaVerificationStatus?.match_status === 'AHA MATCH' || s.ahaVerificationStatus?.match_status === 'MATCH FOUND');
          }
          
          return studentData;
        });
      
      const response = await axios.post(
        `${API}/client-portal/${client.id}/request-booking?email=${encodeURIComponent(email)}`,
        {
          class_type_id: selectedClassType.id,
          start_time: selectedSlot.start,
          end_time: selectedSlot.end,
          students: validStudents,
          class_address: {
            street: bookingAddress.street,
            city: bookingAddress.city,
            state: bookingAddress.state,
            zip: bookingAddress.zip
          },
          location_name: bookingLocationName.trim() || null,
          notes: bookingNotes.trim() || null,
          reference_code: customBookingCode.trim() || null
        }
      );
      
      if (response.data.success) {
        setBookingSuccess({
          reference_code: response.data.reference_code,
          message: response.data.message
        });
        // Reset form
        setSelectedClassType(null);
        setSelectedDate(undefined);
        setSelectedSlot(null);
        setBookingStudents([{ id: Date.now(), firstName: '', lastName: '', email: '', phone: '', ahaEcardCode: '', ahaVerified: false, ahaVerificationStatus: null, errors: {} }]);
        setBookingAddress({ street: '', city: '', state: 'NV', zip: '' });
        setBookingLocationName('');
        setBookingNotes('');
        setCustomBookingCode('');
        setTermsAccepted(false);
        // Refresh bookings
        await fetchClientData(client.id, email);
        toast.success('Booking request submitted!');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error submitting booking request');
    } finally {
      setSubmittingBooking(false);
    }
  };

  // Check if class is a BLS Renewal (requires AHA verification)
  const isRenewalClass = (booking) => {
    if (!booking) return false;
    const classTypeName = (booking.class_type_name || '').toLowerCase();
    const classTypeId = (booking.class_type_id || '').toLowerCase();
    return classTypeName.includes('renewal') || classTypeId.includes('renewal');
  };

  // Check if selected class type is a renewal class (for new booking flow)
  const isSelectedClassRenewal = () => {
    if (!selectedClassType) return false;
    const name = (selectedClassType.name || '').toLowerCase();
    const id = (selectedClassType.id || '').toLowerCase();
    return name.includes('renewal') || id.includes('renewal');
  };

  // Verify AHA eCard for BLS Renewal classes
  const verifyAhaEcard = async () => {
    if (!addStudentForm.first_name?.trim() || !addStudentForm.last_name?.trim() || !addStudentForm.aha_ecard_code?.trim()) {
      toast.error('Please enter student name and eCard code');
      return;
    }

    setVerifyingAha(true);
    setAhaVerificationStatus(null);
    setManualVerifyConfirmed(false);

    try {
      const response = await axios.post(`${API}/verify-aha-ecard`, {
        first_name: addStudentForm.first_name?.trim() || '',
        last_name: addStudentForm.last_name?.trim() || '',
        ecard_code: addStudentForm.aha_ecard_code?.trim() || ''
      });

      const verificationResult = response.data;
      const isValid = verificationResult.verified === true;
      const isExactMatch = verificationResult.is_exact_match === true;

      // Check if it's a partial match (verified but not exact)
      if (isValid && !isExactMatch) {
        const enteredName = `${addStudentForm.first_name} ${addStudentForm.last_name}`;
        const ahaName = verificationResult.card_holder_name || verificationResult.aha_returned_name;
        
        setVerifyingAha(false);
        
        // Show custom modal
        setAhaMismatchModal({
          show: true,
          context: 'addStudent',
          studentId: null,
          enteredName: enteredName,
          ahaName: ahaName,
          verificationResult: verificationResult
        });
        return;
      }

      setAhaVerificationStatus(verificationResult);
      
      if (isValid) {
        if (isExactMatch) {
          toast.success('✓ AHA eCard verified - Exact Match!');
        } else {
          toast.success('AHA eCard verified successfully!');
        }
      } else if (verificationResult.match_status === 'MANUAL VERIFICATION REQUIRED' || verificationResult.match_status === 'AHA UNCLAIMED') {
        toast.warning('Manual verification required - please confirm the eCard matches');
      } else {
        toast.error('Verification failed - please check the eCard code');
      }
    } catch (error) {
      console.error('AHA verification error:', error);
      setAhaVerificationStatus({
        match_status: 'ERROR',
        error: error.response?.data?.detail || 'Verification failed'
      });
      toast.error('Error verifying eCard');
    } finally {
      setVerifyingAha(false);
    }
  };

  // Handle AHA mismatch modal actions
  const handleAhaMismatchAccept = () => {
    const { context, studentId, verificationResult } = ahaMismatchModal;
    
    if (context === 'addStudent') {
      // For add student flow
      setAhaVerificationStatus({
        ...verificationResult,
        needs_review: true,
        user_accepted_mismatch: true
      });
      toast.warning('Student accepted with name mismatch - marked for review');
    } else if (context === 'booking') {
      // For new booking flow
      const student = bookingStudents.find(s => s.id === studentId);
      setBookingStudents(bookingStudents.map(s => 
        s.id === studentId ? { 
          ...s, 
          ahaVerified: true,
          ahaVerificationStatus: {
            ...verificationResult,
            needs_review: true
          }
        } : s
      ));
      toast.warning(`${student?.firstName || 'Student'} accepted with name mismatch - marked for review`);
    }
    
    setAhaMismatchModal({ show: false, context: null, studentId: null, enteredName: '', ahaName: '', verificationResult: null });
  };

  const handleAhaMismatchReject = () => {
    const { context, studentId } = ahaMismatchModal;
    
    if (context === 'addStudent') {
      setAhaVerificationStatus(null);
    } else if (context === 'booking') {
      setBookingStudents(bookingStudents.map(s => 
        s.id === studentId ? { ...s, ahaVerified: false, ahaVerificationStatus: null } : s
      ));
    }
    
    toast.info('Please correct the student name and verify again');
    setAhaMismatchModal({ show: false, context: null, studentId: null, enteredName: '', ahaName: '', verificationResult: null });
  };

  // Check if AHA verification is complete and valid
  const isAhaVerificationValid = () => {
    if (!ahaVerificationStatus) return false;
    
    // Valid if verified (exact or partial match accepted)
    if (ahaVerificationStatus.verified === true) return true;
    
    const status = ahaVerificationStatus.match_status || '';
    
    // Valid if match found (backend returns 'AHA MATCH')
    if (status === 'AHA MATCH' || status === 'AHA MATCH - NEEDS REVIEW' || status === 'MATCH FOUND') return true;
    
    // Valid if manual verification required AND user confirmed
    if (status === 'MANUAL VERIFICATION REQUIRED' && manualVerifyConfirmed) return true;
    
    // Valid if AHA unclaimed but user confirms (card exists but not yet claimed on AHA)
    if (status === 'AHA UNCLAIMED' && manualVerifyConfirmed) return true;
    
    return false;
  };

  // Add student to existing booking from client portal
  const handleAddStudentToBooking = async () => {
    if (!addStudentBooking || !client) return;
    
    // Validate form
    if (!addStudentForm.first_name?.trim() || !addStudentForm.last_name?.trim() || 
        !addStudentForm.email?.trim() || !addStudentForm.phone?.trim()) {
      toast.error('Please fill in all student information');
      return;
    }

    // For BLS Renewal classes, require AHA verification
    if (isRenewalClass(addStudentBooking)) {
      if (!addStudentForm.aha_ecard_code?.trim()) {
        toast.error('Please enter the AHA eCard code for verification');
        return;
      }
      if (!isAhaVerificationValid()) {
        toast.error('Please complete AHA eCard verification before adding student');
        return;
      }
    }
    
    setAddingStudent(true);
    setAddStudentResult(null);
    
    try {
      // Build request with AHA data if it's a renewal class
      const requestData = {
        first_name: addStudentForm.first_name?.trim() || '',
        last_name: addStudentForm.last_name?.trim() || '',
        email: addStudentForm.email?.trim() || '',
        phone: addStudentForm.phone?.trim() || ''
      };

      // Add AHA verification data for renewal classes
      if (isRenewalClass(addStudentBooking) && ahaVerificationStatus) {
        requestData.aha_ecard = {
          ecard_code: addStudentForm.aha_ecard_code?.trim() || '',
          verification_name: ahaVerificationStatus.card_holder_name || ahaVerificationStatus.aha_returned_name || ahaVerificationStatus.found_name || '',
          verification_status: ahaVerificationStatus.match_status || 'Not Verified',
          expiration_date: ahaVerificationStatus.expiration_date || null,
          manually_verified: manualVerifyConfirmed
        };
      }

      const response = await axios.post(
        `${API}/client-portal/${client.id}/bookings/${addStudentBooking.id}/add-student?email=${encodeURIComponent(email)}`,
        requestData
      );
      
      setAddStudentResult(response.data);
      
      if (response.data.success) {
        toast.success(response.data.message);
        // Refresh bookings to show the new student
        await fetchClientData(client.id, email);
        // Reset form but keep modal open to show success
        setAddStudentForm({ first_name: '', last_name: '', email: '', phone: '', aha_ecard_code: '' });
        setAhaVerificationStatus(null);
        setManualVerifyConfirmed(false);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error adding student');
      setAddStudentResult({
        success: false,
        message: error.response?.data?.detail || 'Error adding student'
      });
    } finally {
      setAddingStudent(false);
    }
  };

  // Remove student from booking
  const handleRemoveStudent = async (booking, studentIndex) => {
    if (!booking || !client) return;
    
    const student = booking.students[studentIndex];
    if (!student) return;
    
    // Confirm removal
    const studentName = student.name || `${student.first_name || ''} ${student.last_name || ''}`.trim() || 'this student';
    if (!window.confirm(`Are you sure you want to remove ${studentName} from this class?`)) {
      return;
    }
    
    setRemovingStudent({ bookingId: booking.id, studentIndex });
    
    try {
      const response = await axios.delete(
        `${API}/client-portal/${client.id}/bookings/${booking.id}/remove-student/${studentIndex}?email=${encodeURIComponent(email)}`
      );
      
      if (response.data.success) {
        toast.success(response.data.message || 'Student removed successfully');
        // Refresh bookings to show updated roster
        await fetchClientData(client.id, email);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error removing student');
    } finally {
      setRemovingStudent(null);
    }
  };

  // Determine which add student scenario applies
  const getAddStudentScenario = (booking) => {
    if (!booking || booking.is_past) return null;
    
    // Check if booking is approved - students can only be added to approved bookings
    const bookingStatus = (booking.status || '').toLowerCase();
    if (bookingStatus !== 'approved') {
      return {
        type: 'not_approved',
        canAdd: false,
        message: 'Students can be added after the class is approved by admin.'
      };
    }
    
    const hoursUntil = booking.hours_until_class;
    const invoice = booking.invoice;
    
    if (hoursUntil !== null && hoursUntil < 24) {
      return {
        type: 'within_24_hours',
        canAdd: false,
        message: 'Class is within 24 hours. Please contact us directly to add students.'
      };
    }
    
    if (hoursUntil !== null && hoursUntil < 48 && invoice && invoice.status === 'paid') {
      return {
        type: 'invoice_paid',
        canAdd: false,
        message: 'Invoice has been paid. To add a student, payment must be made.',
        showBookingLink: true,
        referenceCode: booking.reference_code
      };
    }
    
    if (hoursUntil !== null && hoursUntil < 48 && invoice && invoice.status !== 'paid') {
      return {
        type: 'invoice_unpaid',
        canAdd: true,
        message: 'Student will be added and invoice will be updated.'
      };
    }
    
    // Default: > 48 hours or no invoice
    return {
      type: 'can_add',
      canAdd: true,
      message: 'Student will be added. Invoice will be sent 48 hours before class.'
    };
  };

  // Apply saved location for booking
  const applyLocationForBooking = (location) => {
    setBookingLocationName(location.name);
    setBookingAddress({
      street: location.address?.street || '',
      city: location.address?.city || '',
      state: location.address?.state || 'NV',
      zip: location.address?.zip || ''
    });
    toast.success(`Using "${location.name}" for this booking`);
  };

  // Show loading state while checking stored session
  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center p-4">
        <div className="text-center">
          <Loader2 className="w-8 h-8 text-teal-500 animate-spin mx-auto mb-4" />
          <p className="text-slate-400">Loading...</p>
        </div>
      </div>
    );
  }

  // Login screen
  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center p-4">
        <Card className="w-full max-w-md bg-slate-800/90 border-teal-600 shadow-2xl">
          <CardHeader className="text-center">
            <div className="mx-auto mb-4 w-16 h-16 bg-teal-600 rounded-full flex items-center justify-center">
              <Building2 className="w-8 h-8 text-white" />
            </div>
            <CardTitle className="text-2xl font-bold text-white">Client Portal</CardTitle>
            <p className="text-slate-400 mt-2">Access your bookings, invoices, and manage your locations</p>
            <p className="text-slate-500 text-sm mt-3">
              This portal is for approved recurring clients only. If you are interested in becoming a recurring client, please contact{' '}
              <a href="mailto:support@biggestlittlecpr.com" className="text-teal-400 hover:text-teal-300 underline">
                support@biggestlittlecpr.com
              </a>
            </p>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleLogin} className="space-y-4">
              <div>
                <Label htmlFor="email" className="text-slate-300">Email</Label>
                <div className="relative mt-1">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                  <Input
                    id="email"
                    type="email"
                    placeholder="Enter your email address"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="pl-10 bg-slate-700 border-slate-600 text-white placeholder:text-slate-500"
                    data-testid="client-portal-email"
                    autoFocus
                  />
                </div>
              </div>
              
              <div>
                <Label htmlFor="password" className="text-slate-300">Password</Label>
                <div className="relative mt-1">
                  <Input
                    id="password"
                    type={showPassword ? "text" : "password"}
                    placeholder="Enter your password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="pl-3 pr-10 bg-slate-700 border-slate-600 text-white placeholder:text-slate-500"
                    data-testid="client-portal-password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-white"
                  >
                    {showPassword ? 'Hide' : 'Show'}
                  </button>
                </div>
              </div>
              
              <Button 
                type="submit" 
                className="w-full bg-teal-600 hover:bg-teal-700 text-white"
                disabled={loading}
                data-testid="client-portal-login-btn"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Signing in...
                  </>
                ) : (
                  <>
                    Sign In
                    <ChevronRight className="w-4 h-4 ml-2" />
                  </>
                )}
              </Button>
            </form>
            
            <div className="mt-4 text-center">
              <p className="text-slate-500 text-sm">
                Forgot your password? Contact your primary account holder or{' '}
                <a href="mailto:support@biggestlittlecpr.com" className="text-teal-400 hover:text-teal-300">
                  Biggest Little CPR
                </a>
              </p>
            </div>
            
            <div className="mt-6 pt-4 border-t border-slate-700">
              <a 
                href="/" 
                className="flex items-center justify-center gap-2 text-slate-400 hover:text-teal-400 transition-colors"
              >
                <ArrowLeft className="w-4 h-4" />
                Back to Booking Page
              </a>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Main portal
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 p-4 md:p-8">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-8 gap-4">
          <div>
            <h1 className="text-3xl font-bold text-white flex items-center gap-3">
              <Building2 className="w-8 h-8 text-teal-400" />
              {client?.company_name || 'Client Portal'}
            </h1>
            <p className="text-slate-400 mt-1">
              Welcome, {isPrimary ? client?.contact_name : (client?.contacts?.[contactIndex]?.first_name + ' ' + client?.contacts?.[contactIndex]?.last_name)}
              {!isPrimary && <span className="ml-2 text-xs bg-slate-700 px-2 py-0.5 rounded">Team Member</span>}
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            <a href="/" className="text-slate-400 hover:text-white transition-colors flex items-center gap-2 text-sm">
              <ArrowLeft className="w-4 h-4" />
              Back to Booking
            </a>
            <Button 
              variant="ghost" 
              size="sm"
              onClick={() => setShowChangePassword(true)}
              className="text-slate-400 hover:text-white"
            >
              <Key className="w-4 h-4 mr-1" />
              Change Password
            </Button>
            <Button 
              variant="outline" 
              size="sm"
              onClick={handleLogout}
              className="border-slate-600 text-slate-300 hover:bg-slate-700"
            >
              Sign Out
            </Button>
          </div>
        </div>

        <Tabs value={activeTab} onValueChange={(value) => {
          setActiveTab(value);
          localStorage.setItem('clientPortalActiveTab', value);
        }} className="space-y-6">
          <TabsList className="bg-slate-800 border border-slate-700 flex-wrap">
            {hasPermission('book_class') && (
              <TabsTrigger value="book" className="data-[state=active]:bg-teal-600 data-[state=active]:text-white">
                <CalendarPlus className="w-4 h-4 mr-2" />
                Book
              </TabsTrigger>
            )}
            {hasPermission('classes') && (
              <TabsTrigger value="bookings" className="data-[state=active]:bg-teal-600 data-[state=active]:text-white">
                <Calendar className="w-4 h-4 mr-2" />
                Classes
              </TabsTrigger>
            )}
            {hasPermission('saved_locations') && (
              <TabsTrigger value="locations" className="data-[state=active]:bg-teal-600 data-[state=active]:text-white">
                <MapPin className="w-4 h-4 mr-2" />
                Locations
              </TabsTrigger>
            )}
            <TabsTrigger value="info" className="data-[state=active]:bg-teal-600 data-[state=active]:text-white">
              <User className="w-4 h-4 mr-2" />
              Contact Info
            </TabsTrigger>
            {(isPrimary || hasPermission('team_contacts')) && (
              <TabsTrigger value="contacts" className="data-[state=active]:bg-teal-600 data-[state=active]:text-white">
                <Users className="w-4 h-4 mr-2" />
                Team
              </TabsTrigger>
            )}
            {hasPermission('class_pricing') && (
              <TabsTrigger value="pricing" className="data-[state=active]:bg-teal-600 data-[state=active]:text-white">
                <DollarSign className="w-4 h-4 mr-2" />
                Class Pricing
              </TabsTrigger>
            )}
            {isPrimary && (
              <TabsTrigger value="billing" className="data-[state=active]:bg-teal-600 data-[state=active]:text-white">
                <CreditCard className="w-4 h-4 mr-2" />
                Billing
              </TabsTrigger>
            )}
            {hasPermission('invoices') && (
              <TabsTrigger value="invoices" className="data-[state=active]:bg-teal-600 data-[state=active]:text-white">
                <FileText className="w-4 h-4 mr-2" />
                Invoices
              </TabsTrigger>
            )}
          </TabsList>

          {/* Request Booking Tab */}
          <TabsContent value="book">
            <Card className="bg-slate-800 border-slate-700">
              <CardHeader>
                <CardTitle className="text-white flex items-center gap-2">
                  <CalendarPlus className="w-5 h-5 text-teal-400" />
                  Request a Class Booking
                  {client?.restrict_bookings && (
                    <span className="ml-2 text-xs px-2 py-1 bg-red-600/30 text-red-300 rounded flex items-center gap-1">
                      <AlertTriangle className="w-3 h-3" />
                      Restricted
                    </span>
                  )}
                  {!client?.restrict_bookings && hasReachedMaxBookings && (
                    <span className="ml-2 text-xs px-2 py-1 bg-amber-600/30 text-amber-300 rounded">
                      {upcomingBookingsCount}/{MAX_UPCOMING_CLASSES} classes scheduled
                    </span>
                  )}
                </CardTitle>
              </CardHeader>
              <CardContent>
                {/* Restricted from booking message */}
                {client?.restrict_bookings ? (
                  <div className="text-center py-8">
                    <div className="mx-auto mb-4 w-16 h-16 bg-red-600/30 rounded-full flex items-center justify-center">
                      <AlertTriangle className="w-8 h-8 text-red-400" />
                    </div>
                    <h3 className="text-xl font-semibold text-white mb-2">Currently Restricted from Booking</h3>
                    <p className="text-slate-400 mb-6">
                      Your account is currently restricted from booking classes through the portal.
                    </p>
                    <div className="p-4 bg-slate-700 rounded-lg border border-slate-600 max-w-md mx-auto">
                      <p className="text-slate-300 text-sm mb-3">
                        <strong>Please contact us directly to book classes:</strong>
                      </p>
                      <div className="space-y-2 text-sm">
                        <p className="text-teal-400">
                          <Phone className="w-4 h-4 inline mr-2" />
                          775-722-8056
                        </p>
                        <p className="text-teal-400">
                          <Mail className="w-4 h-4 inline mr-2" />
                          classes@biggestlittlecpr.com
                        </p>
                      </div>
                    </div>
                  </div>
                ) : hasReachedMaxBookings ? (
                  <div className="text-center py-8">
                    <div className="mx-auto mb-4 w-16 h-16 bg-amber-600/30 rounded-full flex items-center justify-center">
                      <AlertTriangle className="w-8 h-8 text-amber-400" />
                    </div>
                    <h3 className="text-xl font-semibold text-white mb-2">Maximum Advance Bookings Reached</h3>
                    <p className="text-slate-400 mb-4">
                      You currently have <span className="text-amber-400 font-semibold">{upcomingBookingsCount} upcoming classes</span> scheduled.
                    </p>
                    <p className="text-slate-400 mb-6">
                      To maintain scheduling flexibility, we limit online bookings to {MAX_UPCOMING_CLASSES} upcoming classes at a time.
                      Once a class is completed, you&apos;ll be able to book another.
                    </p>
                    <div className="p-4 bg-slate-700 rounded-lg border border-slate-600 max-w-md mx-auto">
                      <p className="text-slate-300 text-sm mb-3">
                        <strong>Need to schedule additional classes?</strong>
                      </p>
                      <p className="text-slate-400 text-sm mb-3">
                        Contact us directly and we&apos;ll be happy to help:
                      </p>
                      <div className="space-y-2 text-sm">
                        <p className="text-teal-400">
                          <Phone className="w-4 h-4 inline mr-2" />
                          775-722-8056
                        </p>
                        <p className="text-teal-400">
                          <Mail className="w-4 h-4 inline mr-2" />
                          classes@biggestlittlecpr.com
                        </p>
                      </div>
                    </div>
                    <Button 
                      onClick={() => {
                        // Switch to the Classes tab to view current bookings
                        const tabsList = document.querySelector('[value="bookings"]');
                        if (tabsList) tabsList.click();
                      }}
                      variant="outline"
                      className="mt-6 border-teal-600 text-teal-400 hover:bg-teal-600 hover:text-white"
                    >
                      <Calendar className="w-4 h-4 mr-2" />
                      View My Upcoming Classes
                    </Button>
                  </div>
                ) : bookingSuccess ? (
                  <div className="text-center py-8">
                    <div className="mx-auto mb-4 w-16 h-16 bg-green-600 rounded-full flex items-center justify-center">
                      <CheckCircle className="w-8 h-8 text-white" />
                    </div>
                    <h3 className="text-xl font-semibold text-white mb-2">Booking Request Submitted!</h3>
                    <p className="text-slate-400 mb-4">
                      Reference Code: <span className="font-mono text-teal-400 text-lg">{bookingSuccess.reference_code}</span>
                    </p>
                    <p className="text-slate-400 mb-6">{bookingSuccess.message}</p>
                    <Button 
                      onClick={() => setBookingSuccess(null)}
                      className="bg-teal-600 hover:bg-teal-700"
                    >
                      Request Another Booking
                    </Button>
                  </div>
                ) : (
                  <div className="space-y-6">
                    {/* Step 1: Select Class Type */}
                    <div>
                      <Label className="text-slate-300 mb-2 block">1. Select Class Type</Label>
                      {classTypes.length === 0 ? (
                        <div className="p-4 bg-amber-900/30 rounded-lg border border-amber-600">
                          <p className="text-amber-300 text-sm">
                            No preferred classes have been set up for your account. Please contact Biggest Little CPR to add classes to your account.
                          </p>
                        </div>
                      ) : (
                        <Select 
                          value={selectedClassType?.id || ''} 
                          onValueChange={(id) => {
                            const ct = classTypes.find(c => c.id === id);
                            setSelectedClassType(ct);
                            setSelectedDate(undefined);
                            setSelectedSlot(null);
                          }}
                        >
                          <SelectTrigger className="bg-slate-700 border-slate-600 text-white">
                            <SelectValue placeholder="Choose a class type..." />
                          </SelectTrigger>
                          <SelectContent className="bg-slate-700 border-slate-600">
                            {classTypes.map(ct => (
                              <SelectItem key={ct.id} value={ct.id} className="text-white hover:bg-slate-600">
                                {ct.name} <span className="text-slate-400">({ct.duration_hours}h)</span>
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      )}
                    </div>

                    {/* Step 2: Select Date */}
                    {selectedClassType && (
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div>
                          <Label className="text-slate-300 mb-2 block">2. Select Date</Label>
                          <div className="bg-slate-700 rounded-lg p-4 border border-slate-600">
                            <DayPicker
                              mode="single"
                              selected={selectedDate}
                              onSelect={(date) => {
                                setSelectedDate(date);
                                setSelectedSlot(null);
                              }}
                              disabled={[{ before: addDays(new Date(), 2) }]}
                              className="!bg-transparent"
                              classNames={{
                                day_selected: 'bg-teal-600 text-white',
                                day_today: 'bg-slate-600 text-white',
                              }}
                            />
                          </div>
                        </div>

                        {/* Step 3: Select Time */}
                        <div>
                          <Label className="text-slate-300 mb-2 block">3. Select Start Time</Label>
                          <div className="bg-slate-700 rounded-lg p-4 border border-slate-600 max-h-80 overflow-y-auto">
                            {loadingAvailability ? (
                              <div className="flex items-center justify-center py-8">
                                <Loader2 className="w-6 h-6 animate-spin text-teal-400" />
                              </div>
                            ) : !selectedDate ? (
                              <p className="text-slate-400 text-center py-8">Select a date first</p>
                            ) : (() => {
                              // Filter to only available slots
                              const availableSlots = (availability?.slots || []).filter(slot => slot.available);
                              return availableSlots.length > 0 ? (
                                <div>
                                  <p className="text-xs text-slate-400 mb-3">Select your preferred start time ({availableSlots.length} available)</p>
                                  <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
                                    {availableSlots.map((slot, idx) => {
                                      // Calculate end time based on class duration
                                      const startTime = new Date(slot.start);
                                      const endTime = new Date(startTime.getTime() + (selectedClassType?.duration_hours || 4) * 3600000);
                                      return (
                                        <button
                                          key={idx}
                                          onClick={() => setSelectedSlot({
                                            start: slot.start,
                                            end: endTime.toISOString()
                                          })}
                                          className={`p-2 rounded-lg text-sm font-medium transition-all ${
                                            selectedSlot?.start === slot.start
                                              ? 'bg-teal-500 text-slate-900 shadow-md ring-2 ring-teal-400'
                                              : 'bg-teal-900/40 text-teal-300 hover:bg-teal-900/60 border border-teal-700'
                                          }`}
                                        >
                                          {formatInTimeZone(new Date(slot.start), 'America/Los_Angeles', 'h:mm a')}
                                        </button>
                                      );
                                    })}
                                  </div>
                                  {selectedSlot && selectedClassType && (
                                    <div className="mt-3 p-2 bg-teal-900/30 rounded border border-teal-700">
                                      <p className="text-sm text-teal-300">
                                        <strong>Class Duration:</strong> {selectedClassType.duration_hours} hours
                                      </p>
                                      <p className="text-xs text-slate-400 mt-1">
                                        {formatInTimeZone(new Date(selectedSlot.start), 'America/Los_Angeles', 'h:mm a')} - {formatInTimeZone(new Date(selectedSlot.end), 'America/Los_Angeles', 'h:mm a')}
                                      </p>
                                    </div>
                                  )}
                                </div>
                              ) : (
                                <p className="text-slate-400 text-center py-8">No available times for this date. Please select another date.</p>
                              );
                            })()}
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Step 4: Location */}
                    {selectedSlot && (
                      <div>
                        <Label className="text-slate-300 mb-2 block">4. Class Location</Label>
                        
                        {/* Quick select from saved locations */}
                        {client?.locations?.length > 0 && (
                          <div className="mb-3">
                            <p className="text-xs text-slate-500 mb-2">Use a saved location:</p>
                            <div className="flex flex-wrap gap-2">
                              {client.locations.map(loc => (
                                <Button
                                  key={loc.id}
                                  variant="outline"
                                  size="sm"
                                  onClick={() => applyLocationForBooking(loc)}
                                  className="border-teal-600/50 text-teal-400 hover:bg-teal-600/20"
                                >
                                  <MapPin className="w-3 h-3 mr-1" />
                                  {loc.name}
                                </Button>
                              ))}
                            </div>
                          </div>
                        )}
                        
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                          <div>
                            <Label className="text-xs text-slate-400">Location Name (optional)</Label>
                            <Input
                              placeholder="e.g., Main Office"
                              value={bookingLocationName}
                              onChange={(e) => setBookingLocationName(e.target.value)}
                              className="mt-1 bg-slate-700 border-slate-600 text-white"
                            />
                          </div>
                          <div className="md:col-span-2">
                            <Label className="text-xs text-slate-400">Address</Label>
                            <AddressAutocomplete
                              initialValue={bookingAddress.street ? `${bookingAddress.street}, ${bookingAddress.city}, ${bookingAddress.state} ${bookingAddress.zip}` : ''}
                              placeholder="Search for address..."
                              className="mt-1 bg-slate-700 border-slate-600 text-white"
                              onAddressSelect={(addr) => {
                                if (addr) {
                                  setBookingAddress({
                                    street: addr.street || '',
                                    city: addr.city || '',
                                    state: addr.state || 'NV',
                                    zip: addr.zip || ''
                                  });
                                }
                              }}
                            />
                          </div>
                        </div>
                        {bookingAddress.street && (
                          <div className="mt-2 p-2 bg-green-900/30 rounded border border-green-700 flex items-center gap-2">
                            <CheckCircle className="w-4 h-4 text-green-400" />
                            <span className="text-sm text-green-300">
                              {bookingAddress.street}, {bookingAddress.city}, {bookingAddress.state} {bookingAddress.zip}
                            </span>
                          </div>
                        )}
                      </div>
                    )}

                    {/* Step 5: Students (Optional) */}
                    {selectedSlot && bookingAddress.street && (
                      <div>
                        <Label className="text-slate-300 mb-2 block flex items-center gap-2">
                          <Users className="w-4 h-4 text-teal-400" />
                          5. Student Information
                          <span className="text-xs font-normal text-slate-500">(optional - can add later)</span>
                        </Label>
                        <p className="text-xs text-slate-500 mb-3">
                          You can add students now or leave blank and add them later using the booking code.
                        </p>
                        <div className="space-y-4">
                          {bookingStudents.map((student, index) => (
                            <div key={student.id} className="p-4 bg-slate-700 rounded-lg border border-slate-600">
                              <div className="flex items-center justify-between mb-3">
                                <span className="text-sm font-semibold text-white">Student {index + 1}</span>
                                {bookingStudents.length > 1 && (
                                  <button
                                    type="button"
                                    onClick={() => removeBookingStudent(student.id)}
                                    className="text-red-400 hover:text-red-300 p-1"
                                  >
                                    <Trash2 className="h-4 w-4" />
                                  </button>
                                )}
                              </div>
                              <div className="grid grid-cols-2 gap-3">
                                <div>
                                  <Label className="text-xs font-medium text-slate-400 mb-1 block">
                                    First Name
                                  </Label>
                                  <Input
                                    placeholder="John"
                                    value={student.firstName}
                                    onChange={(e) => updateBookingStudent(student.id, 'firstName', e.target.value)}
                                    className={`h-9 text-sm bg-slate-800 border-slate-600 text-slate-100 placeholder:text-slate-500 ${student.errors?.firstName ? 'border-red-500' : ''}`}
                                  />
                                  {student.errors?.firstName && (
                                    <p className="text-xs text-red-400 mt-1">{student.errors.firstName}</p>
                                  )}
                                </div>
                                <div>
                                  <Label className="text-xs font-medium text-slate-400 mb-1 block">
                                    Last Name
                                  </Label>
                                  <Input
                                    placeholder="Doe"
                                    value={student.lastName}
                                    onChange={(e) => updateBookingStudent(student.id, 'lastName', e.target.value)}
                                    className={`h-9 text-sm bg-slate-800 border-slate-600 text-slate-100 placeholder:text-slate-500 ${student.errors?.lastName ? 'border-red-500' : ''}`}
                                  />
                                  {student.errors?.lastName && (
                                    <p className="text-xs text-red-400 mt-1">{student.errors.lastName}</p>
                                  )}
                                </div>
                                <div className="col-span-2">
                                  <Label className="text-xs font-medium text-slate-400 mb-1 block">
                                    Email
                                  </Label>
                                  <Input
                                    type="email"
                                    placeholder="john@example.com"
                                    value={student.email}
                                    onChange={(e) => updateBookingStudent(student.id, 'email', e.target.value)}
                                    className={`h-9 text-sm bg-slate-800 border-slate-600 text-slate-100 placeholder:text-slate-500 ${student.errors?.email ? 'border-red-500' : ''}`}
                                  />
                                  {student.errors?.email && (
                                    <p className="text-xs text-red-400 mt-1">{student.errors.email}</p>
                                  )}
                                </div>
                                <div className="col-span-2">
                                  <Label className="text-xs font-medium text-slate-400 mb-1 block">
                                    Phone
                                  </Label>
                                  <Input
                                    type="tel"
                                    placeholder="(555) 123-4567"
                                    value={student.phone}
                                    onChange={(e) => updateBookingStudent(student.id, 'phone', formatPhoneNumber(e.target.value))}
                                    className={`h-9 text-sm bg-slate-800 border-slate-600 text-slate-100 placeholder:text-slate-500 ${student.errors?.phone ? 'border-red-500' : ''}`}
                                  />
                                  {student.errors?.phone && (
                                    <p className="text-xs text-red-400 mt-1">{student.errors.phone}</p>
                                  )}
                                </div>

                                {/* AHA eCard Verification for Renewal Classes */}
                                {isSelectedClassRenewal() && (
                                  <div className="col-span-2 mt-3 p-3 bg-amber-900/30 border border-amber-600/50 rounded-lg">
                                    <Label className="text-xs font-medium text-amber-400 mb-2 block flex items-center gap-2">
                                      <AlertTriangle className="w-4 h-4" />
                                      BLS Renewal Verification Required
                                    </Label>
                                    <p className="text-xs text-amber-300/80 mb-3">
                                      Each student must provide their AHA eCard code for verification.
                                    </p>
                                    <div className="flex gap-2">
                                      <Input
                                        placeholder="Enter eCard code (e.g., 225511223344)"
                                        value={student.ahaEcardCode || ''}
                                        onChange={(e) => updateBookingStudent(student.id, 'ahaEcardCode', e.target.value)}
                                        className="h-9 text-sm bg-slate-800 border-slate-600 text-slate-100 placeholder:text-slate-500 flex-1"
                                      />
                                      <Button
                                        type="button"
                                        onClick={() => verifyStudentAhaEcard(student.id)}
                                        disabled={!student.firstName || !student.lastName || !student.ahaEcardCode || student.ahaVerificationStatus?.verifying}
                                        className="h-9 bg-amber-600 hover:bg-amber-700 text-white"
                                      >
                                        {student.ahaVerificationStatus?.verifying ? (
                                          <Loader2 className="w-4 h-4 animate-spin" />
                                        ) : (
                                          'Verify'
                                        )}
                                      </Button>
                                    </div>

                                    {/* Verification Result */}
                                    {student.ahaVerificationStatus && !student.ahaVerificationStatus.verifying && (
                                      <div className={`mt-3 p-2 rounded-lg border ${
                                        student.ahaVerificationStatus.match_status === 'AHA MATCH' || student.ahaVerificationStatus.match_status === 'MATCH FOUND'
                                          ? 'bg-green-900/30 border-green-600/50'
                                          : student.ahaVerificationStatus.match_status === 'MANUAL VERIFICATION REQUIRED' || student.ahaVerificationStatus.match_status === 'AHA UNCLAIMED'
                                          ? 'bg-yellow-900/30 border-yellow-600/50'
                                          : 'bg-red-900/30 border-red-600/50'
                                      }`}>
                                        <div className="flex items-center gap-2">
                                          {student.ahaVerificationStatus.match_status === 'AHA MATCH' || student.ahaVerificationStatus.match_status === 'MATCH FOUND' ? (
                                            <CheckCircle className="w-4 h-4 text-green-400" />
                                          ) : student.ahaVerificationStatus.match_status === 'MANUAL VERIFICATION REQUIRED' || student.ahaVerificationStatus.match_status === 'AHA UNCLAIMED' ? (
                                            <AlertTriangle className="w-4 h-4 text-yellow-400" />
                                          ) : (
                                            <AlertCircle className="w-4 h-4 text-red-400" />
                                          )}
                                          <span className={`text-sm font-medium ${
                                            student.ahaVerificationStatus.match_status === 'AHA MATCH' || student.ahaVerificationStatus.match_status === 'MATCH FOUND'
                                              ? 'text-green-400'
                                              : student.ahaVerificationStatus.match_status === 'MANUAL VERIFICATION REQUIRED' || student.ahaVerificationStatus.match_status === 'AHA UNCLAIMED'
                                              ? 'text-yellow-400'
                                              : 'text-red-400'
                                          }`}>
                                            {student.ahaVerificationStatus.match_status === 'AHA MATCH' || student.ahaVerificationStatus.match_status === 'MATCH FOUND'
                                              ? '✓ Verified' 
                                              : student.ahaVerificationStatus.match_status === 'MANUAL VERIFICATION REQUIRED' || student.ahaVerificationStatus.match_status === 'AHA UNCLAIMED'
                                              ? '⚠ Manual Verification Required'
                                              : student.ahaVerificationStatus.error || '✗ Verification Failed'}
                                          </span>
                                        </div>
                                        {(student.ahaVerificationStatus.card_holder_name || student.ahaVerificationStatus.aha_returned_name) && (
                                          <p className="text-xs text-slate-300 mt-1 ml-6">
                                            Name: {student.ahaVerificationStatus.card_holder_name || student.ahaVerificationStatus.aha_returned_name}
                                          </p>
                                        )}
                                        {student.ahaVerificationStatus.expiration_date && (
                                          <p className="text-xs text-slate-300 ml-6">
                                            Expires: {student.ahaVerificationStatus.expiration_date}
                                          </p>
                                        )}
                                        
                                        {/* Manual verification checkbox */}
                                        {(student.ahaVerificationStatus.match_status === 'MANUAL VERIFICATION REQUIRED' || student.ahaVerificationStatus.match_status === 'AHA UNCLAIMED') && (
                                          <label className="flex items-center gap-2 mt-2 ml-6 cursor-pointer">
                                            <input
                                              type="checkbox"
                                              checked={student.ahaVerified}
                                              onChange={(e) => updateBookingStudent(student.id, 'ahaVerified', e.target.checked)}
                                              className="w-4 h-4 rounded border-slate-500 bg-slate-700 text-amber-500 focus:ring-amber-500"
                                            />
                                            <span className="text-xs text-yellow-300">
                                              I confirm this eCard is valid
                                            </span>
                                          </label>
                                        )}
                                      </div>
                                    )}
                                  </div>
                                )}
                              </div>
                            </div>
                          ))}
                          
                          <Button
                            type="button"
                            variant="outline"
                            onClick={addBookingStudent}
                            className="w-full border-dashed border-teal-600 text-teal-400 hover:bg-teal-600/20"
                          >
                            <Plus className="w-4 h-4 mr-2" />
                            Add Another Student
                          </Button>
                        </div>
                      </div>
                    )}

                    {/* Step 6: Notes */}
                    {selectedSlot && bookingAddress.street && (
                      <div>
                        <Label className="text-slate-300 mb-2 block">6. Additional Notes (optional)</Label>
                        <Textarea
                          placeholder="Any special requirements or notes..."
                          value={bookingNotes}
                          onChange={(e) => setBookingNotes(e.target.value)}
                          className="bg-slate-700 border-slate-600 text-white"
                          rows={3}
                        />
                      </div>
                    )}

                    {/* Step 7: Custom Booking Code */}
                    {selectedSlot && bookingAddress.street && (
                      <div>
                        <Label className="text-slate-300 mb-2 block">
                          7. Custom Booking Code <span className="text-slate-500 font-normal">(optional)</span>
                        </Label>
                        <Input
                          placeholder="e.g., teamname, mycompany2026, etc."
                          value={customBookingCode}
                          onChange={(e) => setCustomBookingCode(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ''))}
                          className="bg-slate-700 border-slate-600 text-white font-mono"
                          maxLength={20}
                        />
                        <p className="text-xs text-slate-500 mt-1">
                          Create your own booking code for easy reference, or leave blank to auto-generate one. 
                          Only lowercase letters, numbers, and dashes allowed.
                        </p>
                      </div>
                    )}

                    {/* Live Pricing Preview */}
                    {selectedClassType && livePricing && (
                      <div className="bg-slate-700/50 rounded-lg p-4 border border-slate-600">
                        <h4 className="text-sm font-semibold text-teal-400 mb-3 flex items-center gap-2">
                          <FileText className="w-4 h-4" />
                          Estimated Total {livePricing.isEstimate && <span className="text-xs text-slate-400 font-normal">(estimate)</span>}
                        </h4>
                        {livePricing.studentCount > 0 ? (
                          <div className="space-y-2 text-sm">
                            <div className="flex justify-between text-slate-300">
                              <span>{livePricing.studentCount} student{livePricing.studentCount !== 1 ? 's' : ''} × ${livePricing.basePrice?.toFixed(2)}</span>
                              <span>${livePricing.studentTotal?.toFixed(2)}</span>
                            </div>
                            {livePricing.travelFee > 0 && (
                              <div className="flex justify-between text-slate-400">
                                <span>Travel fee (outside Reno/Sparks)</span>
                                <span>${livePricing.travelFee?.toFixed(2)}</span>
                              </div>
                            )}
                            <div className="flex justify-between text-white font-bold pt-2 border-t border-slate-600">
                              <span>Total</span>
                              <span className="text-teal-400">${livePricing.total?.toFixed(2)}</span>
                            </div>
                          </div>
                        ) : (
                          <p className="text-sm text-amber-400">Please add at least one student with complete information to see pricing.</p>
                        )}
                        {livePricing.studentCount > 0 && livePricing.customPrice !== null && livePricing.customPrice !== undefined && (
                          <p className="text-xs text-emerald-400 mt-2">✓ Your custom pricing applied</p>
                        )}
                      </div>
                    )}

                    {/* Terms & Submit */}
                    {selectedSlot && bookingAddress.street && (
                      <div className="pt-4 border-t border-slate-700 space-y-4">
                        {/* Terms Acceptance */}
                        <div className="bg-slate-700/50 rounded-lg p-4 border border-slate-600">
                          <h4 className="text-sm font-semibold text-white mb-3">Terms & Conditions</h4>
                          <div className="text-xs text-slate-400 space-y-2 mb-4">
                            <p>By submitting this booking request, you agree to:</p>
                            <ul className="list-disc list-inside space-y-1 ml-2">
                              <li><strong className="text-slate-300">Travel Fees:</strong> Classes held outside Reno/Sparks may incur travel fees based on distance.</li>
                              <li><strong className="text-slate-300">Rush Fees:</strong> Bookings made within 48 hours of class start may incur a rush fee.</li>
                              <li><strong className="text-slate-300">Late-Add Fees:</strong> Students added within 24 hours of class start may incur an additional fee.</li>
                              <li><strong className="text-slate-300">Payment:</strong> An invoice will be sent 48 hours before class. Payment is due before class begins.</li>
                            </ul>
                          </div>
                          <label className="flex items-center gap-3 cursor-pointer">
                            <input
                              type="checkbox"
                              checked={termsAccepted}
                              onChange={(e) => setTermsAccepted(e.target.checked)}
                              className="w-5 h-5 rounded border-slate-500 bg-slate-600 text-teal-500 focus:ring-teal-500"
                            />
                            <span className="text-sm text-slate-300">
                              I accept these terms and conditions
                            </span>
                          </label>
                        </div>

                        {/* Payment Notice */}
                        <div className="bg-amber-900/30 p-4 rounded-lg border border-amber-600">
                          <div className="flex items-start gap-3">
                            <AlertTriangle className="w-5 h-5 text-amber-400 flex-shrink-0 mt-0.5" />
                            <div>
                              <p className="text-amber-300 font-medium">Payment Information</p>
                              <p className="text-amber-200/80 text-sm mt-1">
                                Payment will be invoiced or collected as agreed with Biggest Little CPR.
                                You will receive an invoice 48 hours before your class.
                              </p>
                            </div>
                          </div>
                        </div>
                        
                        <Button
                          onClick={handleSubmitBookingRequest}
                          disabled={submittingBooking || !termsAccepted}
                          className="w-full bg-teal-600 hover:bg-teal-700 text-white h-12 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          {submittingBooking ? (
                            <>
                              <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                              Submitting Request...
                            </>
                          ) : (
                            <>
                              <CalendarPlus className="w-5 h-5 mr-2" />
                              Submit Booking Request
                            </>
                          )}
                        </Button>
                        {!termsAccepted && (
                          <p className="text-xs text-center text-slate-500">Please accept the terms above to submit</p>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Bookings Tab */}
          <TabsContent value="bookings">
            <Card className="bg-slate-800 border-slate-700">
              <CardHeader>
                <CardTitle className="text-white flex items-center gap-2">
                  <Calendar className="w-5 h-5 text-teal-400" />
                  Class Bookings
                  <span className="ml-2 text-sm font-normal text-slate-400">
                    ({bookings.filter(b => !b.is_past).length} upcoming, {bookings.filter(b => b.is_past).length} past)
                  </span>
                </CardTitle>
              </CardHeader>
              <CardContent>
                {bookings.length === 0 ? (
                  <p className="text-slate-400 text-center py-8">No bookings found</p>
                ) : (
                  <div className="space-y-4">
                    {/* Upcoming Classes - sorted by date (soonest first) */}
                    {bookings.filter(b => !b.is_past).length > 0 && (
                      <div>
                        <h3 className="text-sm font-semibold text-teal-400 mb-3 uppercase tracking-wide">Upcoming Classes</h3>
                        <div className="space-y-3">
                          {bookings
                            .filter(b => !b.is_past)
                            .sort((a, b) => new Date(a.start_time) - new Date(b.start_time))
                            .map((booking) => {
                            const isExpanded = expandedBookings[booking.id];
                            return (
                              <div key={booking.id} className="bg-slate-700 rounded-lg border border-teal-600/50 overflow-hidden">
                                {/* Collapsed view - always visible */}
                                <button
                                  onClick={() => toggleBookingExpanded(booking.id)}
                                  className="w-full p-4 flex items-center justify-between text-left hover:bg-slate-600/50 transition-colors"
                                >
                                  <div className="flex items-center gap-4">
                                    <div className="flex items-center gap-2 text-slate-300">
                                      <Calendar className="w-4 h-4 text-teal-400" />
                                      <span>{formatDate(booking.start_time)}</span>
                                    </div>
                                    <div className="flex items-center gap-2 text-slate-400">
                                      <Clock className="w-4 h-4" />
                                      <span>{formatTime(booking.start_time)}</span>
                                    </div>
                                    {booking.location_name && (
                                      <div className="flex items-center gap-2 text-slate-400">
                                        <MapPin className="w-4 h-4" />
                                        <span>{booking.location_name}</span>
                                      </div>
                                    )}
                                  </div>
                                  <div className="flex items-center gap-3">
                                    {/* Payment status badge */}
                                    {booking.payment_status === 'paid' ? (
                                      <span className="text-xs px-2 py-1 bg-green-600/30 text-green-300 rounded flex items-center gap-1">
                                        <CheckCircle className="w-3 h-3" />
                                        Paid
                                      </span>
                                    ) : (
                                      <span className="text-xs px-2 py-1 bg-amber-600/30 text-amber-300 rounded flex items-center gap-1">
                                        <Clock className="w-3 h-3" />
                                        Unpaid
                                      </span>
                                    )}
                                    <span className="text-xs px-2 py-1 bg-teal-600/30 text-teal-300 rounded">
                                      {booking.student_count} student{booking.student_count !== 1 ? 's' : ''}
                                    </span>
                                    {isExpanded ? (
                                      <ChevronUp className="w-5 h-5 text-slate-400" />
                                    ) : (
                                      <ChevronDown className="w-5 h-5 text-slate-400" />
                                    )}
                                  </div>
                                </button>
                                
                                {/* Expanded details */}
                                {isExpanded && (
                                  <div className="px-4 pb-4 border-t border-slate-600">
                                    <div className="pt-4 space-y-3">
                                      <div>
                                        <p className="text-white font-semibold">{booking.class_type_name}</p>
                                        {booking.location_address && (
                                          <p className="text-slate-400 text-sm mt-1 flex items-center gap-1">
                                            <MapPin className="w-4 h-4" />
                                            {booking.location_address}
                                          </p>
                                        )}
                                      </div>
                                      
                                      {/* Booking Code and Add Student */}
                                      {booking.reference_code && (
                                        <div className="space-y-2">
                                          <div className="flex items-center gap-3">
                                            <span className="text-slate-400 text-sm">Booking Code:</span>
                                            <span className="font-mono text-teal-400 bg-slate-600 px-2 py-1 rounded">{booking.reference_code}</span>
                                          </div>
                                          
                                          {/* Add Student Button with Scenario Handling */}
                                          {(() => {
                                            const scenario = getAddStudentScenario(booking);
                                            if (!scenario) return null;
                                            
                                            if (scenario.type === 'not_approved') {
                                              return (
                                                <div className="p-3 bg-slate-700/50 border border-slate-600 rounded-lg">
                                                  <div className="flex items-center gap-2 text-slate-400">
                                                    <Clock className="w-4 h-4" />
                                                    <span className="text-sm">{scenario.message}</span>
                                                  </div>
                                                </div>
                                              );
                                            }
                                            
                                            if (scenario.type === 'within_24_hours') {
                                              return (
                                                <div className="p-3 bg-red-900/30 border border-red-600/50 rounded-lg">
                                                  <div className="flex items-center gap-2 text-red-300">
                                                    <AlertCircle className="w-4 h-4" />
                                                    <span className="text-sm">{scenario.message}</span>
                                                  </div>
                                                </div>
                                              );
                                            }
                                            
                                            if (scenario.type === 'invoice_paid') {
                                              return (
                                                <div className="p-3 bg-amber-900/30 border border-amber-600/50 rounded-lg">
                                                  <p className="text-amber-300 text-sm mb-2">{scenario.message}</p>
                                                  <a
                                                    href={`/?code=${booking.reference_code}`}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    className="inline-flex items-center gap-2 px-3 py-1.5 bg-teal-600 hover:bg-teal-700 text-white rounded text-sm font-medium"
                                                  >
                                                    <Plus className="w-4 h-4" />
                                                    Add Student with Payment
                                                    <ExternalLink className="w-3 h-3" />
                                                  </a>
                                                </div>
                                              );
                                            }
                                            
                                            // Can add student directly
                                            return (
                                              <Button
                                                size="sm"
                                                onClick={() => {
                                                  setAddStudentBooking(booking);
                                                  setAddStudentForm({ first_name: '', last_name: '', email: '', phone: '', aha_ecard_code: '' });
                                                  setAddStudentResult(null);
                                                  setAhaVerificationStatus(null);
                                                  setManualVerifyConfirmed(false);
                                                }}
                                                className="bg-teal-600 hover:bg-teal-700 text-white"
                                              >
                                                <Plus className="w-4 h-4 mr-1" />
                                                Add Student
                                              </Button>
                                            );
                                          })()}
                                        </div>
                                      )}
                                      
                                      {/* Payment Status */}
                                      <div className={`p-3 rounded-lg border ${
                                        booking.payment_status === 'paid' 
                                          ? 'bg-green-900/30 border-green-600/50' 
                                          : 'bg-amber-900/30 border-amber-600/50'
                                      }`}>
                                        {/* Simple student count display */}
                                        <div className="flex items-center justify-between">
                                          <span className="text-sm text-slate-300">
                                            {booking.student_count || booking.students?.length || 0} Student{(booking.student_count || booking.students?.length || 0) !== 1 ? 's' : ''} Enrolled
                                          </span>
                                          {booking.total_price > 0 && (
                                            <span className="text-sm text-slate-400">
                                              Est. ${booking.total_price?.toFixed(2)}
                                            </span>
                                          )}
                                        </div>
                                        
                                        {/* Invoice reference if exists */}
                                        {booking.invoice && (
                                          <div className="mt-2 pt-2 border-t border-slate-600/50 text-xs text-slate-400">
                                            Invoice #{booking.invoice.invoice_id} - {booking.invoice.status === 'paid' ? 'Paid' : 'Pending'}
                                          </div>
                                        )}
                                        
                                        {/* Note about automatic billing */}
                                        {!booking.invoice && !booking.is_past && booking.status === 'approved' && (
                                          <p className="mt-2 text-xs text-slate-500">
                                            Payment will be processed automatically 48 hours before class
                                          </p>
                                        )}
                                      </div>
                                      
                                      {/* Students - simple list with add/remove only */}
                                      {booking.students && booking.students.length > 0 && (
                                        <div>
                                          <p className="text-xs text-slate-500 mb-2">Students:</p>
                                          <div className="space-y-1">
                                            {booking.students.map((student, idx) => (
                                              <div key={idx} className="flex items-center justify-between text-sm group">
                                                <span className="text-slate-300">{student.name || `${student.first_name || ''} ${student.last_name || ''}`.trim()}</span>
                                                {/* Remove button - only show for upcoming approved classes */}
                                                {!booking.is_past && booking.status === 'approved' && hasPermission('book_class') && (
                                                  <button
                                                    onClick={(e) => {
                                                      e.stopPropagation();
                                                      handleRemoveStudent(booking, idx);
                                                    }}
                                                    disabled={removingStudent?.bookingId === booking.id && removingStudent?.studentIndex === idx}
                                                    className="opacity-0 group-hover:opacity-100 transition-opacity p-0.5 text-red-400 hover:text-red-300 hover:bg-red-900/30 rounded"
                                                    title="Remove student"
                                                  >
                                                    {removingStudent?.bookingId === booking.id && removingStudent?.studentIndex === idx ? (
                                                      <Loader2 className="w-3.5 h-3.5 animate-spin" />
                                                    ) : (
                                                      <Trash2 className="w-3.5 h-3.5" />
                                                    )}
                                                  </button>
                                                )}
                                              </div>
                                            ))}
                                          </div>
                                        </div>
                                      )}
                                    </div>
                                  </div>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}
                    
                    {/* Past Classes - Collapsible Section */}
                    {bookings.filter(b => b.is_past).length > 0 && (
                      <div className="mt-6">
                        <button
                          onClick={() => setShowPastClasses(!showPastClasses)}
                          className="w-full flex items-center justify-between py-2 px-3 bg-slate-700/50 rounded-lg hover:bg-slate-700/70 transition-colors"
                        >
                          <h3 className="text-sm font-semibold text-slate-500 uppercase tracking-wide flex items-center gap-2">
                            <CheckCircle className="w-4 h-4" />
                            Past Classes ({bookings.filter(b => b.is_past).length})
                          </h3>
                          {showPastClasses ? (
                            <ChevronUp className="w-5 h-5 text-slate-500" />
                          ) : (
                            <ChevronDown className="w-5 h-5 text-slate-500" />
                          )}
                        </button>
                        
                        {showPastClasses && (
                        <div className="space-y-3 mt-3">
                          {bookings
                            .filter(b => b.is_past)
                            .sort((a, b) => new Date(b.start_time) - new Date(a.start_time))
                            .map((booking) => {
                            const isExpanded = expandedBookings[booking.id];
                            return (
                              <div key={booking.id} className="bg-slate-700/50 rounded-lg border border-slate-600/50 overflow-hidden">
                                {/* Collapsed view */}
                                <button
                                  onClick={() => toggleBookingExpanded(booking.id)}
                                  className="w-full p-4 flex items-center justify-between text-left hover:bg-slate-600/30 transition-colors"
                                >
                                  <div className="flex items-center gap-4">
                                    <div className="flex items-center gap-2 text-slate-400">
                                      <Calendar className="w-4 h-4" />
                                      <span>{formatDate(booking.start_time)}</span>
                                    </div>
                                    <div className="flex items-center gap-2 text-slate-500">
                                      <Clock className="w-4 h-4" />
                                      <span>{formatTime(booking.start_time)}</span>
                                    </div>
                                    {booking.location_name && (
                                      <div className="flex items-center gap-2 text-slate-500">
                                        <MapPin className="w-4 h-4" />
                                        <span>{booking.location_name}</span>
                                      </div>
                                    )}
                                  </div>
                                  <div className="flex items-center gap-3">
                                    <span className="text-xs px-2 py-1 bg-slate-600/50 text-slate-400 rounded">
                                      {booking.student_count} student{booking.student_count !== 1 ? 's' : ''}
                                    </span>
                                    {isExpanded ? (
                                      <ChevronUp className="w-5 h-5 text-slate-500" />
                                    ) : (
                                      <ChevronDown className="w-5 h-5 text-slate-500" />
                                    )}
                                  </div>
                                </button>
                                
                                {/* Expanded details */}
                                {isExpanded && (
                                  <div className="px-4 pb-4 border-t border-slate-600/50">
                                    <div className="pt-4 space-y-3">
                                      <p className="text-slate-300 font-semibold">{booking.class_type_name}</p>
                                      
                                      {/* Booking Code */}
                                      {booking.reference_code && (
                                        <div className="flex items-center gap-3">
                                          <span className="text-slate-500 text-sm">Booking Code:</span>
                                          <span className="font-mono text-slate-400 bg-slate-600/50 px-2 py-1 rounded">{booking.reference_code}</span>
                                        </div>
                                      )}
                                      
                                      {/* Students with attendance status */}
                                      {booking.students && booking.students.length > 0 && (
                                        <div>
                                          <p className="text-xs text-slate-500 mb-2">Students:</p>
                                          <div className="space-y-1">
                                            {booking.students.map((student, idx) => (
                                              <div key={idx} className="flex items-center gap-2 text-sm">
                                                <span className="text-slate-400">{student.name}</span>
                                                <span className={`px-2 py-0.5 rounded text-xs ${
                                                  student.status === 'attended' 
                                                    ? 'bg-green-600/30 text-green-300 border border-green-600/50' 
                                                    : 'bg-red-600/30 text-red-300 border border-red-600/50'
                                                }`}>
                                                  {student.status === 'attended' ? 'Attended' : 'Absent'}
                                                </span>
                                              </div>
                                            ))}
                                          </div>
                                        </div>
                                      )}
                                    </div>
                                  </div>
                                )}
                              </div>
                            );
                          })}
                        </div>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Locations Tab */}
          <TabsContent value="locations">
            <Card className="bg-slate-800 border-slate-700">
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle className="text-white flex items-center gap-2">
                  <MapPin className="w-5 h-5 text-teal-400" />
                  Saved Locations
                </CardTitle>
                {!editingLocations ? (
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={() => {
                      setLocationsForm(client?.locations || []);
                      setEditingLocations(true);
                    }}
                    className="border-teal-600 text-teal-400 hover:bg-teal-600 hover:text-white"
                  >
                    <Edit2 className="w-4 h-4 mr-2" />
                    Edit Locations
                  </Button>
                ) : (
                  <div className="flex gap-2">
                    <Button 
                      variant="outline" 
                      size="sm"
                      onClick={() => {
                        setEditingLocations(false);
                        setLocationsForm(client?.locations || []);
                      }}
                      className="border-slate-600 text-slate-300"
                    >
                      <X className="w-4 h-4 mr-1" />
                      Cancel
                    </Button>
                    <Button 
                      size="sm"
                      onClick={handleSaveLocations}
                      disabled={savingLocations}
                      className="bg-teal-600 hover:bg-teal-700 text-white"
                    >
                      {savingLocations ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Save className="w-4 h-4 mr-1" />}
                      Save
                    </Button>
                  </div>
                )}
              </CardHeader>
              <CardContent>
                {editingLocations ? (
                  <div className="space-y-4">
                    {locationsForm.length === 0 ? (
                      <p className="text-slate-400 text-center py-4">No locations added yet</p>
                    ) : (
                      locationsForm.map((location, idx) => {
                        const formattedAddress = location.address?.street 
                          ? `${location.address.street}, ${location.address.city || ''}, ${location.address.state || ''} ${location.address.zip || ''}`.replace(/,\s*,/g, ',').replace(/,\s*$/, '')
                          : '';
                        
                        return (
                          <div key={location.id || idx} className="p-4 bg-slate-700 rounded-lg border border-slate-600">
                            <div className="flex items-start justify-between gap-3">
                              <div className="flex-1 space-y-3">
                                <Input
                                  placeholder="Location Name (e.g., Main Office)"
                                  value={location.name || ''}
                                  onChange={(e) => {
                                    const newLocations = [...locationsForm];
                                    newLocations[idx] = { ...newLocations[idx], name: e.target.value };
                                    setLocationsForm(newLocations);
                                  }}
                                  className="bg-slate-600 border-slate-500 text-white"
                                />
                                
                                <AddressAutocomplete
                                  initialValue={formattedAddress}
                                  placeholder="Search for address..."
                                  className="bg-slate-600 border-slate-500 text-white"
                                  onAddressSelect={(addressComponents, formatted, placeName) => {
                                    if (!addressComponents) return;
                                    
                                    const newLocations = [...locationsForm];
                                    newLocations[idx] = {
                                      ...newLocations[idx],
                                      address: {
                                        street: addressComponents.street || '',
                                        city: addressComponents.city || '',
                                        state: addressComponents.state || '',
                                        zip: addressComponents.zip || ''
                                      }
                                    };
                                    if (!newLocations[idx].name && placeName) {
                                      newLocations[idx].name = placeName;
                                    }
                                    setLocationsForm(newLocations);
                                    toast.success('Address verified');
                                  }}
                                />
                                
                                {location.address?.street && (
                                  <div className="p-2 bg-green-900/30 rounded border border-green-700 flex items-center gap-2">
                                    <CheckCircle className="w-4 h-4 text-green-400" />
                                    <span className="text-sm text-green-300">
                                      {location.address.street}, {location.address.city}, {location.address.state} {location.address.zip}
                                    </span>
                                  </div>
                                )}
                              </div>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => {
                                  const newLocations = locationsForm.filter((_, i) => i !== idx);
                                  setLocationsForm(newLocations);
                                }}
                                className="text-red-400 hover:text-red-300 hover:bg-red-900/30"
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </div>
                          </div>
                        );
                      })
                    )}
                    
                    <Button
                      variant="outline"
                      onClick={() => {
                        setLocationsForm([
                          ...locationsForm,
                          { id: `temp-${Date.now()}`, name: '', address: { street: '', city: '', state: '', zip: '' } }
                        ]);
                      }}
                      className="w-full border-dashed border-teal-600 text-teal-400 hover:bg-teal-600/20"
                    >
                      <Plus className="w-4 h-4 mr-2" />
                      Add Location
                    </Button>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {(!client?.locations || client.locations.length === 0) ? (
                      <p className="text-slate-400 text-center py-8">No saved locations. Click &quot;Edit Locations&quot; to add one.</p>
                    ) : (
                      client.locations.map((location) => (
                        <div key={location.id} className="p-4 bg-slate-700 rounded-lg border border-slate-600 flex items-start gap-4">
                          <div className="w-10 h-10 rounded-full bg-teal-600/20 flex items-center justify-center flex-shrink-0">
                            <MapPin className="w-5 h-5 text-teal-400" />
                          </div>
                          <div>
                            <p className="text-white font-medium">{location.name}</p>
                            <p className="text-slate-400 text-sm">
                              {location.address?.street}, {location.address?.city}, {location.address?.state} {location.address?.zip}
                            </p>
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Contact Info Tab */}
          <TabsContent value="info">
            <Card className="bg-slate-800 border-slate-700">
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle className="text-white flex items-center gap-2">
                  <User className="w-5 h-5 text-teal-400" />
                  {isPrimary ? 'Point of Contact Information' : 'Your Contact Information'}
                </CardTitle>
                {!editingInfo ? (
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={() => setEditingInfo(true)}
                    className="border-teal-600 text-teal-400 hover:bg-teal-600 hover:text-white"
                  >
                    <Edit2 className="w-4 h-4 mr-2" />
                    Edit
                  </Button>
                ) : (
                  <div className="flex gap-2">
                    <Button 
                      variant="outline" 
                      size="sm"
                      onClick={() => {
                        setEditingInfo(false);
                        setInfoForm({
                          first_name: client.first_name || '',
                          last_name: client.last_name || '',
                          title: client.title || '',
                          phone: client.phone || '',
                          company: client.company || ''
                        });
                      }}
                      className="border-slate-600 text-slate-300"
                    >
                      <X className="w-4 h-4 mr-1" />
                      Cancel
                    </Button>
                    <Button 
                      size="sm"
                      onClick={handleSaveInfo}
                      disabled={savingInfo}
                      className="bg-teal-600 hover:bg-teal-700 text-white"
                    >
                      {savingInfo ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Save className="w-4 h-4 mr-1" />}
                      Save
                    </Button>
                  </div>
                )}
              </CardHeader>
              <CardContent>
                {/* Secondary user info banner */}
                {!isPrimary && primaryContact && (
                  <div className="mb-4 p-3 bg-slate-700 rounded-lg border border-slate-600">
                    <p className="text-slate-400 text-sm">
                      You are logged in as a secondary contact for <span className="text-white font-medium">{client?.company}</span>.
                      Primary contact: <span className="text-teal-400">{primaryContact.first_name} {primaryContact.last_name}</span> ({primaryContact.email})
                    </p>
                  </div>
                )}
                
                {editingInfo ? (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Label className="text-slate-300">First Name</Label>
                      <Input
                        value={infoForm.first_name}
                        onChange={(e) => setInfoForm({ ...infoForm, first_name: e.target.value })}
                        className="mt-1 bg-slate-700 border-slate-600 text-white"
                      />
                    </div>
                    <div>
                      <Label className="text-slate-300">Last Name</Label>
                      <Input
                        value={infoForm.last_name}
                        onChange={(e) => setInfoForm({ ...infoForm, last_name: e.target.value })}
                        className="mt-1 bg-slate-700 border-slate-600 text-white"
                      />
                    </div>
                    <div>
                      <Label className="text-slate-300">Title / Position</Label>
                      <Input
                        value={infoForm.title}
                        onChange={(e) => setInfoForm({ ...infoForm, title: e.target.value })}
                        placeholder="Safety Director"
                        className="mt-1 bg-slate-700 border-slate-600 text-white"
                      />
                    </div>
                    {isPrimary && (
                      <div>
                        <Label className="text-slate-300">Company</Label>
                        <Input
                          value={infoForm.company}
                          onChange={(e) => setInfoForm({ ...infoForm, company: e.target.value })}
                          className="mt-1 bg-slate-700 border-slate-600 text-white"
                        />
                      </div>
                    )}
                    <div className={isPrimary ? "md:col-span-2" : ""}>
                      <Label className="text-slate-300">Phone</Label>
                      <Input
                        value={infoForm.phone}
                        onChange={(e) => setInfoForm({ ...infoForm, phone: e.target.value })}
                        className="mt-1 bg-slate-700 border-slate-600 text-white"
                      />
                    </div>
                  </div>
                ) : (
                  <div className="space-y-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-slate-700 flex items-center justify-center">
                          <User className="w-5 h-5 text-teal-400" />
                        </div>
                        <div>
                          <p className="text-xs text-slate-500 uppercase">Name</p>
                          <p className="text-white font-medium">{client?.first_name} {client?.last_name}</p>
                          {client?.title && (
                            <p className="text-slate-400 text-sm">{client.title}</p>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-slate-700 flex items-center justify-center">
                          <Building2 className="w-5 h-5 text-teal-400" />
                        </div>
                        <div>
                          <p className="text-xs text-slate-500 uppercase">Company</p>
                          <p className="text-white font-medium">{client?.company || 'Not provided'}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-slate-700 flex items-center justify-center">
                          <Mail className="w-5 h-5 text-teal-400" />
                        </div>
                        <div className="flex-1">
                          <p className="text-xs text-slate-500 uppercase">Email (Portal Login)</p>
                          <div className="flex items-center gap-2">
                            {isPrimary && editingEmail ? (
                              <div className="flex items-center gap-2 flex-1">
                                <Input
                                  type="email"
                                  value={newEmail}
                                  onChange={(e) => setNewEmail(e.target.value)}
                                  placeholder="New email address"
                                  className="bg-slate-700 border-slate-600 text-white h-8"
                                />
                                <Button
                                  size="sm"
                                  onClick={() => {
                                    if (newEmail.trim()) {
                                      setShowEmailConfirm(true);
                                    }
                                  }}
                                  className="bg-teal-600 hover:bg-teal-700 h-8"
                                >
                                  Save
                                </Button>
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  onClick={() => {
                                    setEditingEmail(false);
                                    setNewEmail('');
                                  }}
                                  className="text-slate-400 h-8"
                                >
                                  Cancel
                                </Button>
                              </div>
                            ) : (
                              <>
                                <p className="text-white font-medium">{client?.email}</p>
                                {isPrimary && (
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => {
                                      setEditingEmail(true);
                                      setNewEmail(client?.email || '');
                                    }}
                                    className="text-teal-400 hover:text-teal-300 h-6 px-2"
                                  >
                                    <Edit2 className="w-3 h-3" />
                                  </Button>
                                )}
                              </>
                            )}
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-slate-700 flex items-center justify-center">
                          <Phone className="w-5 h-5 text-teal-400" />
                        </div>
                        <div>
                          <p className="text-xs text-slate-500 uppercase">Phone</p>
                          <p className="text-white font-medium">{client?.phone || 'Not provided'}</p>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Payment Methods Tab (Primary users only) */}
          {isPrimary && (
            <TabsContent value="billing">
              <Card className="bg-slate-800 border-slate-700">
                <CardHeader>
                  <CardTitle className="text-white flex items-center gap-2">
                    <CreditCard className="w-5 h-5 text-teal-400" />
                    Payment Method
                  </CardTitle>
                  <p className="text-slate-400 text-sm">
                    Save a card for automatic payment of invoices 48 hours before your classes.
                  </p>
                </CardHeader>
                <CardContent className="space-y-6">
                  {!vaultEnabled ? (
                    /* Coming Soon Notice - shown when vault is disabled by admin */
                    <div className="p-4 bg-amber-900/30 border border-amber-600/50 rounded-lg">
                      <div className="flex items-start gap-3">
                        <div className="w-10 h-10 rounded-full bg-amber-600/20 flex items-center justify-center flex-shrink-0">
                          <Clock className="w-5 h-5 text-amber-400" />
                        </div>
                        <div>
                          <h4 className="text-amber-300 font-semibold">Coming Soon</h4>
                          <p className="text-amber-200/80 text-sm mt-1">
                            Saved payment methods and automatic billing are coming soon. 
                            For now, you will continue to receive invoices via email which you can pay online.
                          </p>
                        </div>
                      </div>
                    </div>
                  ) : paymentMethodLoading ? (
                    <div className="flex items-center gap-2 text-slate-400">
                      <Loader2 className="w-5 h-5 animate-spin" />
                      <span>Loading payment method...</span>
                    </div>
                  ) : paymentMethod?.has_payment_method ? (
                    <>
                      {/* Saved Card Display */}
                      <div className="p-4 bg-gradient-to-br from-slate-700 to-slate-800 rounded-lg border border-slate-600">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-4">
                            <div className="w-12 h-8 bg-slate-600 rounded flex items-center justify-center">
                              {paymentMethod.card_brand?.toLowerCase() === 'visa' && (
                                <span className="text-blue-400 font-bold text-sm">VISA</span>
                              )}
                              {paymentMethod.card_brand?.toLowerCase() === 'mastercard' && (
                                <span className="text-orange-400 font-bold text-xs">MC</span>
                              )}
                              {paymentMethod.card_brand?.toLowerCase() === 'amex' && (
                                <span className="text-blue-300 font-bold text-xs">AMEX</span>
                              )}
                              {!['visa', 'mastercard', 'amex'].includes(paymentMethod.card_brand?.toLowerCase()) && (
                                <CreditCard className="w-5 h-5 text-slate-400" />
                              )}
                            </div>
                            <div>
                              <p className="text-white font-medium">
                                {paymentMethod.card_brand || 'Card'} •••• {paymentMethod.card_last_four}
                              </p>
                              {paymentMethod.card_expiry && (
                                <p className="text-slate-400 text-sm">Expires {paymentMethod.card_expiry}</p>
                              )}
                            </div>
                          </div>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={handleDeletePaymentMethod}
                            disabled={deletingPaymentMethod}
                            className="text-red-400 hover:text-red-300 hover:bg-red-900/20"
                          >
                            {deletingPaymentMethod ? (
                              <Loader2 className="w-4 h-4 animate-spin" />
                            ) : (
                              <Trash2 className="w-4 h-4" />
                            )}
                          </Button>
                        </div>
                        {paymentMethod.saved_at && (
                          <p className="text-slate-500 text-xs mt-2">
                            Added {new Date(paymentMethod.saved_at).toLocaleDateString()}
                          </p>
                        )}
                      </div>

                      {/* Auto-Charge Toggle */}
                      <div className="p-4 bg-slate-700/50 rounded-lg">
                        <div className="flex items-start justify-between gap-4">
                          <div>
                            <p className="text-white font-medium">Automatic Payment</p>
                            <p className="text-slate-400 text-sm mt-1">
                              {paymentMethod.auto_charge_enabled 
                                ? 'Your card will be automatically charged 48 hours before each class.'
                                : 'You will receive an invoice 48 hours before each class instead of automatic payment.'}
                            </p>
                          </div>
                          <Button
                            variant={paymentMethod.auto_charge_enabled ? "default" : "outline"}
                            size="sm"
                            onClick={handleToggleAutoCharge}
                            disabled={togglingAutoCharge}
                            className={paymentMethod.auto_charge_enabled 
                              ? "bg-teal-600 hover:bg-teal-700"
                              : "border-slate-600 text-slate-300 hover:bg-slate-700"}
                          >
                            {togglingAutoCharge ? (
                              <Loader2 className="w-4 h-4 animate-spin mr-2" />
                            ) : paymentMethod.auto_charge_enabled ? (
                              <Check className="w-4 h-4 mr-2" />
                            ) : null}
                            {paymentMethod.auto_charge_enabled ? 'Enabled' : 'Disabled'}
                          </Button>
                        </div>
                      </div>

                      {/* Change Card Button */}
                      <Button
                        variant="outline"
                        onClick={handleAddPaymentMethod}
                        disabled={addingPaymentMethod}
                        className="border-slate-600 text-slate-300 hover:bg-slate-700"
                      >
                        {addingPaymentMethod ? (
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        ) : (
                          <RefreshCw className="w-4 h-4 mr-2" />
                        )}
                        Replace Card
                      </Button>
                    </>
                  ) : (
                    <>
                      {/* No Card - Add Card Prompt */}
                      <div className="text-center py-8">
                        <div className="w-16 h-16 mx-auto mb-4 bg-slate-700 rounded-full flex items-center justify-center">
                          <CreditCard className="w-8 h-8 text-slate-400" />
                        </div>
                        <h3 className="text-white font-medium mb-2">No Payment Method Saved</h3>
                        <p className="text-slate-400 text-sm mb-6 max-w-md mx-auto">
                          Add a card to enable automatic payment for your classes. 
                          Your card will be securely stored by Stripe and charged 48 hours before each scheduled class.
                        </p>
                        <Button
                          onClick={handleAddPaymentMethod}
                          disabled={addingPaymentMethod}
                          className="bg-teal-600 hover:bg-teal-700"
                        >
                          {addingPaymentMethod ? (
                            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          ) : (
                            <Plus className="w-4 h-4 mr-2" />
                          )}
                          Add Payment Method
                        </Button>
                      </div>

                      {/* Info Box */}
                      <div className="p-4 bg-blue-900/20 border border-blue-700/50 rounded-lg">
                        <p className="text-blue-300 text-sm">
                          <strong>How it works:</strong> When you add a card, invoices for your classes will be 
                          automatically paid 48 hours before the class date. You&apos;ll receive a receipt via email. 
                          If payment fails, you&apos;ll receive an invoice to pay manually.
                        </p>
                      </div>
                    </>
                  )}
                </CardContent>
              </Card>
            </TabsContent>
          )}

          {/* Team Contacts Tab (Primary users and team members with permission) */}
          {(isPrimary || hasPermission('team_contacts')) && (
            <TabsContent value="contacts">
              <Card className="bg-slate-800 border-slate-700">
                <CardHeader>
                  <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
                    <CardTitle className="text-white flex items-center gap-2">
                      <Users className="w-5 h-5 text-teal-400" />
                      Team Members
                      <span className="ml-2 text-sm font-normal text-slate-400">
                        ({(client?.contacts || []).length} additional)
                      </span>
                    </CardTitle>
                    <div className="flex gap-2">
                      {/* Change Primary - Primary only */}
                      {isPrimary && client?.contacts?.length > 0 && (
                        <Button
                          onClick={() => setShowSwapPrimary(true)}
                          variant="outline"
                          size="sm"
                          className="border-amber-500/50 text-amber-400 hover:bg-amber-500/10"
                        >
                          <UserCog className="w-4 h-4 mr-2" />
                          Change Primary
                        </Button>
                      )}
                      <Button
                        onClick={() => {
                          setShowAddContact(true);
                          setContactForm({ first_name: '', last_name: '', email: '', phone: '', title: '', temporary_password: '' });
                        }}
                        size="sm"
                        className="bg-teal-600 hover:bg-teal-700"
                      >
                        <Plus className="w-4 h-4 mr-2" />
                        Add Team Member
                      </Button>
                    </div>
                  </div>
                  <p className="text-slate-400 text-sm mt-2">
                    {isPrimary 
                      ? 'Add team members who can access this portal. You can set their permissions and manage their passwords.'
                      : 'Manage team members who can access this portal. You can add members and set their permissions.'}
                  </p>
                </CardHeader>
                <CardContent className="space-y-4">
                  {/* Primary Contact */}
                  <div className="p-4 bg-teal-900/30 rounded-lg border border-teal-600">
                    <div className="flex items-start justify-between">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-teal-600 rounded-full flex items-center justify-center">
                          <User className="w-5 h-5 text-white" />
                        </div>
                        <div>
                          <p className="text-white font-semibold">
                            {client?.contact_name}
                            <span className="ml-2 px-2 py-0.5 text-xs bg-teal-600 text-white rounded-full">Primary</span>
                          </p>
                          <p className="text-slate-400 text-sm">{client?.email}</p>
                          {client?.phone && <p className="text-slate-400 text-sm">{client.phone}</p>}
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Secondary Contacts */}
                  {(client?.contacts || []).length === 0 ? (
                    <p className="text-slate-400 text-center py-4">No additional team members yet</p>
                  ) : (
                    (client?.contacts || []).map((contact) => (
                      <div key={contact.id} className="p-4 bg-slate-700 rounded-lg border border-slate-600">
                        {isPrimary && editingContact === contact.id ? (
                          // Edit mode
                          <div className="space-y-3">
                            <div className="grid grid-cols-2 gap-3">
                              <div>
                                <Label className="text-slate-300 text-xs">First Name *</Label>
                                <Input
                                  value={contactForm.first_name}
                                  onChange={(e) => setContactForm({ ...contactForm, first_name: e.target.value })}
                                  className="bg-slate-600 border-slate-500 text-white"
                                />
                              </div>
                              <div>
                                <Label className="text-slate-300 text-xs">Last Name *</Label>
                                <Input
                                  value={contactForm.last_name}
                                  onChange={(e) => setContactForm({ ...contactForm, last_name: e.target.value })}
                                  className="bg-slate-600 border-slate-500 text-white"
                                />
                              </div>
                            </div>
                            <div>
                              <Label className="text-slate-300 text-xs">Email *</Label>
                              <Input
                                type="email"
                                value={contactForm.email}
                                onChange={(e) => setContactForm({ ...contactForm, email: e.target.value })}
                                className="bg-slate-600 border-slate-500 text-white"
                              />
                            </div>
                            <div className="grid grid-cols-2 gap-3">
                              <div>
                                <Label className="text-slate-300 text-xs">Phone</Label>
                                <Input
                                  value={contactForm.phone}
                                  onChange={(e) => setContactForm({ ...contactForm, phone: e.target.value })}
                                  className="bg-slate-600 border-slate-500 text-white"
                                />
                              </div>
                              <div>
                                <Label className="text-slate-300 text-xs">Title/Role</Label>
                                <Input
                                  value={contactForm.title}
                                  onChange={(e) => setContactForm({ ...contactForm, title: e.target.value })}
                                  className="bg-slate-600 border-slate-500 text-white"
                                  placeholder="e.g., Training Coordinator"
                                />
                              </div>
                            </div>
                            <div className="flex gap-2 pt-2">
                              <Button
                                onClick={() => handleUpdateContact(contact.id)}
                                disabled={savingContact}
                                size="sm"
                                className="bg-teal-600 hover:bg-teal-700"
                              >
                                {savingContact ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4 mr-1" />}
                                Save
                              </Button>
                              <Button
                                onClick={() => {
                                  setEditingContact(null);
                                  setContactForm({ first_name: '', last_name: '', email: '', phone: '', title: '' });
                                }}
                                variant="outline"
                                size="sm"
                                className="border-slate-500 text-slate-300"
                              >
                                Cancel
                              </Button>
                            </div>
                          </div>
                        ) : (
                          // View mode
                          <div className="flex items-start justify-between">
                            <div className="flex items-center gap-3">
                              <div className="w-10 h-10 bg-slate-600 rounded-full flex items-center justify-center">
                                <User className="w-5 h-5 text-slate-300" />
                              </div>
                              <div>
                                <p className="text-white font-semibold">{contact.first_name} {contact.last_name}</p>
                                {contact.title && <p className="text-slate-400 text-sm">{contact.title}</p>}
                                <p className="text-slate-400 text-sm">{contact.email}</p>
                                {contact.phone && <p className="text-slate-400 text-sm">{contact.phone}</p>}
                                {/* Show password status - only for primary */}
                                {isPrimary && (
                                  contact.password_hash ? (
                                    <span className="inline-flex items-center gap-1 text-xs text-green-400 mt-1">
                                      <Lock className="w-3 h-3" /> Password set
                                      {contact.must_change_password && <span className="text-amber-400">(change required)</span>}
                                    </span>
                                  ) : (
                                    <span className="inline-flex items-center gap-1 text-xs text-amber-400 mt-1">
                                      <AlertTriangle className="w-3 h-3" /> No password set
                                    </span>
                                  )
                                )}
                              </div>
                            </div>
                            {/* Action buttons */}
                            <div className="flex flex-wrap gap-1">
                              <Button
                                onClick={() => {
                                  setEditingContact(contact.id);
                                  setContactForm({
                                    first_name: contact.first_name || '',
                                    last_name: contact.last_name || '',
                                    email: contact.email || '',
                                    phone: contact.phone || '',
                                    title: contact.title || ''
                                  });
                                }}
                                variant="ghost"
                                size="sm"
                                className="text-slate-400 hover:text-white"
                                title="Edit contact"
                              >
                                <Edit2 className="w-4 h-4" />
                              </Button>
                              {/* Reset password - Primary only */}
                              {isPrimary && (
                                <Button
                                  onClick={() => {
                                    const idx = client.contacts.findIndex(c => c.id === contact.id);
                                    setResetPasswordContact({ ...contact, index: idx });
                                  }}
                                  variant="ghost"
                                  size="sm"
                                  className="text-amber-400 hover:text-amber-300"
                                  title="Reset password"
                                >
                                  <Key className="w-4 h-4" />
                                </Button>
                              )}
                              {/* Edit permissions - Primary only */}
                              {isPrimary && (
                                <Button
                                  onClick={() => {
                                    const idx = client.contacts.findIndex(c => c.id === contact.id);
                                    setEditPermissionsIndex(idx);
                                    setTempPermissions(contact.permissions || DEFAULT_PERMISSIONS);
                                    setEditPermissionsContact(contact);
                                  }}
                                  variant="ghost"
                                  size="sm"
                                  className="text-teal-400 hover:text-teal-300"
                                  title="Edit permissions"
                                >
                                  <Shield className="w-4 h-4" />
                                </Button>
                              )}
                              <Button
                                onClick={() => handleDeleteContact(contact.id)}
                                variant="ghost"
                                size="sm"
                                className="text-red-400 hover:text-red-300"
                                title="Delete contact"
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </div>
                          </div>
                        )}
                      </div>
                    ))
                  )}

                  {/* Add Contact Form */}
                  {showAddContact && (
                    <div className="p-4 bg-slate-700 rounded-lg border-2 border-teal-600 border-dashed">
                      <h4 className="text-white font-semibold mb-3">Add New Team Member</h4>
                      <div className="space-y-3">
                        <div className="grid grid-cols-2 gap-3">
                          <div>
                            <Label className="text-slate-300 text-xs">First Name *</Label>
                            <Input
                              value={contactForm.first_name}
                              onChange={(e) => setContactForm({ ...contactForm, first_name: e.target.value })}
                              className="bg-slate-600 border-slate-500 text-white"
                              placeholder="John"
                            />
                          </div>
                          <div>
                            <Label className="text-slate-300 text-xs">Last Name *</Label>
                            <Input
                              value={contactForm.last_name}
                              onChange={(e) => setContactForm({ ...contactForm, last_name: e.target.value })}
                              className="bg-slate-600 border-slate-500 text-white"
                              placeholder="Doe"
                            />
                          </div>
                        </div>
                        <div>
                          <Label className="text-slate-300 text-xs">Email * (They will use this to log in)</Label>
                          <Input
                            type="email"
                            value={contactForm.email}
                            onChange={(e) => setContactForm({ ...contactForm, email: e.target.value })}
                            className="bg-slate-600 border-slate-500 text-white"
                            placeholder="john@company.com"
                          />
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                          <div>
                            <Label className="text-slate-300 text-xs">Phone</Label>
                            <Input
                              value={contactForm.phone}
                              onChange={(e) => setContactForm({ ...contactForm, phone: e.target.value })}
                              className="bg-slate-600 border-slate-500 text-white"
                              placeholder="(555) 123-4567"
                            />
                          </div>
                          <div>
                            <Label className="text-slate-300 text-xs">Title/Role</Label>
                            <Input
                              value={contactForm.title}
                              onChange={(e) => setContactForm({ ...contactForm, title: e.target.value })}
                              className="bg-slate-600 border-slate-500 text-white"
                              placeholder="e.g., Training Coordinator"
                            />
                          </div>
                        </div>
                        {/* Temporary Password - Primary only */}
                        {isPrimary && (
                          <div>
                            <Label className="text-slate-300 text-xs">
                              Temporary Password * (They will be prompted to change on first login)
                            </Label>
                            <Input
                              type="text"
                              value={contactForm.temporary_password}
                              onChange={(e) => setContactForm({ ...contactForm, temporary_password: e.target.value })}
                              className="bg-slate-600 border-slate-500 text-white"
                              placeholder="Min 8 chars, uppercase, lowercase, number, special char"
                            />
                            {contactForm.temporary_password && (
                              <div className="mt-1 text-xs">
                                {validatePassword(contactForm.temporary_password).length === 0 ? (
                                  <span className="text-green-400">✓ Password meets requirements</span>
                                ) : (
                                  <span className="text-amber-400">Missing: {validatePassword(contactForm.temporary_password).join(', ')}</span>
                                )}
                              </div>
                            )}
                          </div>
                        )}
                        {!isPrimary && (
                          <div className="p-3 bg-slate-600 rounded-lg text-sm text-slate-300">
                            <p>Note: The primary contact will need to set a password for this team member before they can log in.</p>
                          </div>
                        )}
                        <div className="flex gap-2 pt-2">
                          <Button
                            onClick={handleAddContact}
                            disabled={savingContact || (isPrimary && (!contactForm.temporary_password || validatePassword(contactForm.temporary_password).length > 0))}
                            className="bg-teal-600 hover:bg-teal-700"
                          >
                            {savingContact ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Plus className="w-4 h-4 mr-2" />}
                            Add Team Member
                          </Button>
                          <Button
                            onClick={() => {
                              setShowAddContact(false);
                              setContactForm({ first_name: '', last_name: '', email: '', phone: '', title: '', temporary_password: '' });
                            }}
                            variant="outline"
                            className="border-slate-500 text-slate-300"
                          >
                            Cancel
                          </Button>
                        </div>
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>
          )}

          {/* Class Pricing Tab */}
          <TabsContent value="pricing">
            <Card className="bg-slate-800 border-slate-700">
              <CardHeader>
                <CardTitle className="text-white flex items-center gap-2">
                  <DollarSign className="w-5 h-5 text-teal-400" />
                  Your Class Pricing
                </CardTitle>
                <p className="text-slate-400 text-sm mt-1">
                  Special pricing negotiated for your organization. Prices are locked in when you book.
                </p>
              </CardHeader>
              <CardContent>
                {client?.preferred_classes && client.preferred_classes.length > 0 ? (
                  <div className="space-y-3">
                    {client.preferred_classes.map((pc) => {
                      const classType = classTypes.find(c => c.id === pc.class_id);
                      if (!classType) return null;
                      
                      const hasCustomPrice = pc.custom_price !== null && pc.custom_price !== undefined;
                      const regularPrice = classType.woo_price || classType.base_price || classType.price || 0;
                      const savings = hasCustomPrice ? regularPrice - pc.custom_price : 0;
                      const hasExpiry = pc.price_expires_at;
                      const expiryDate = hasExpiry ? new Date(pc.price_expires_at) : null;
                      const isExpired = expiryDate && expiryDate <= new Date();
                      const expiresIn = expiryDate ? Math.ceil((expiryDate - new Date()) / (1000 * 60 * 60 * 24)) : null;
                      
                      return (
                        <div 
                          key={pc.class_id}
                          className={`p-4 rounded-lg border ${
                            hasCustomPrice && !isExpired 
                              ? 'bg-green-900/20 border-green-700' 
                              : 'bg-slate-700 border-slate-600'
                          }`}
                        >
                          <div className="flex flex-col md:flex-row md:items-center justify-between gap-3">
                            <div className="flex-1">
                              <h4 className="text-white font-semibold text-lg">{classType.name}</h4>
                              {classType.description && (
                                <p className="text-slate-400 text-sm mt-1">{classType.description}</p>
                              )}
                            </div>
                            
                            <div className="flex flex-col items-end gap-1">
                              {/* Regular Price */}
                              <div className="flex items-center gap-2">
                                <span className="text-slate-500 text-sm">Regular Price:</span>
                                <span className={`text-lg ${hasCustomPrice && !isExpired ? 'line-through text-slate-500' : 'text-white font-semibold'}`}>
                                  ${regularPrice.toFixed(2)}
                                </span>
                              </div>
                              
                              {/* Your Price (if custom) */}
                              {hasCustomPrice && !isExpired && (
                                <div className="flex items-center gap-2">
                                  <span className="text-green-400 text-sm">Your Price:</span>
                                  <span className="text-2xl font-bold text-green-400">
                                    ${pc.custom_price.toFixed(2)}
                                  </span>
                                  {savings > 0 && (
                                    <span className="px-2 py-0.5 bg-green-600 text-white text-xs rounded-full">
                                      Save ${savings.toFixed(2)}
                                    </span>
                                  )}
                                </div>
                              )}
                              
                              {/* Expired custom price */}
                              {hasCustomPrice && isExpired && (
                                <div className="flex items-center gap-2">
                                  <span className="text-amber-400 text-sm">Special pricing expired</span>
                                </div>
                              )}
                              
                              {/* No custom price */}
                              {!hasCustomPrice && (
                                <div className="flex items-center gap-2">
                                  <Tag className="w-4 h-4 text-slate-400" />
                                  <span className="text-slate-400 text-sm">Standard pricing</span>
                                </div>
                              )}
                            </div>
                          </div>
                          
                          {/* Expiration Notice */}
                          {hasCustomPrice && hasExpiry && !isExpired && (
                            <div className={`mt-3 pt-3 border-t ${expiresIn <= 7 ? 'border-amber-700' : 'border-green-700/50'}`}>
                              <div className={`flex items-center gap-2 ${expiresIn <= 7 ? 'text-amber-400' : 'text-slate-400'}`}>
                                <Clock className="w-4 h-4" />
                                <span className="text-sm">
                                  {expiresIn <= 0 
                                    ? 'Expires today!' 
                                    : expiresIn === 1 
                                      ? 'Expires tomorrow' 
                                      : `Expires in ${expiresIn} days (${expiryDate.toLocaleDateString()})`
                                  }
                                </span>
                                {expiresIn <= 7 && (
                                  <span className="px-2 py-0.5 bg-amber-600 text-white text-xs rounded-full">
                                    Book soon!
                                  </span>
                                )}
                              </div>
                            </div>
                          )}
                          
                          {/* No expiration */}
                          {hasCustomPrice && !hasExpiry && !isExpired && (
                            <div className="mt-3 pt-3 border-t border-green-700/50">
                              <div className="flex items-center gap-2 text-green-400">
                                <CheckCircle className="w-4 h-4" />
                                <span className="text-sm">No expiration - this rate is ongoing</span>
                              </div>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <div className="text-center py-8">
                    <DollarSign className="w-12 h-12 text-slate-600 mx-auto mb-3" />
                    <p className="text-slate-400">No special pricing configured for your organization.</p>
                    <p className="text-slate-500 text-sm mt-1">Contact us to discuss custom rates for your team.</p>
                  </div>
                )}
                
                {/* Pricing Notes */}
                <div className="mt-6 p-4 bg-slate-700/50 rounded-lg border border-slate-600">
                  <h4 className="text-white font-medium mb-2 flex items-center gap-2">
                    <AlertCircle className="w-4 h-4 text-teal-400" />
                    Pricing Notes
                  </h4>
                  <ul className="text-slate-400 text-sm space-y-1">
                    <li>• Prices are locked in at the time of booking</li>
                    <li>• Even if a special rate expires, your existing bookings keep their original price</li>
                    <li>• Contact support if you have questions about your pricing</li>
                  </ul>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Invoices Tab */}
          <TabsContent value="invoices">
            <Card className="bg-slate-800 border-slate-700">
              <CardHeader>
                <CardTitle className="text-white flex items-center gap-2">
                  <FileText className="w-5 h-5 text-teal-400" />
                  Invoices
                  <span className="ml-2 text-sm font-normal text-slate-400">({invoices.length} total)</span>
                </CardTitle>
              </CardHeader>
              <CardContent>
                {invoices.length === 0 ? (
                  <p className="text-slate-400 text-center py-8">No invoices found</p>
                ) : (
                  <div className="space-y-3">
                    {invoices.map((invoice) => (
                      <a 
                        key={invoice.invoice_id} 
                        href={`/invoice/${invoice.invoice_id}`}
                        className="block p-4 bg-slate-700 rounded-lg border border-slate-600 hover:border-teal-600 transition-colors"
                      >
                        <div className="flex flex-col md:flex-row md:items-center justify-between gap-3">
                          <div>
                            <div className="flex items-center gap-2">
                              <p className="text-white font-semibold">Invoice #{invoice.invoice_id}</p>
                              <span className={`px-2 py-0.5 text-xs rounded-full ${
                                invoice.status === 'paid' ? 'bg-green-600 text-white' :
                                invoice.status === 'sent' ? 'bg-blue-600 text-white' :
                                invoice.status === 'overdue' ? 'bg-red-600 text-white' :
                                'bg-slate-600 text-slate-300'
                              }`}>
                                {invoice.status}
                              </span>
                            </div>
                            <p className="text-slate-400 text-sm mt-1">
                              Created: {formatDate(invoice.created_at)} • {invoice.items_summary}
                            </p>
                          </div>
                          <div className="text-right">
                            <p className="text-white font-semibold text-lg">${invoice.total?.toFixed(2)}</p>
                            {invoice.amount_due > 0 && (
                              <p className="text-sm text-amber-400">Due: ${invoice.amount_due?.toFixed(2)}</p>
                            )}
                          </div>
                        </div>
                      </a>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>

      {/* Email Change Confirmation Dialog */}
      <Dialog open={showEmailConfirm} onOpenChange={setShowEmailConfirm}>
        <DialogContent className="bg-slate-800 border-slate-700">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-amber-400" />
              Confirm Email Change
            </DialogTitle>
            <DialogDescription className="text-slate-400">
              You are about to change your email address from <span className="text-white font-medium">{client?.email}</span> to <span className="text-teal-400 font-medium">{newEmail}</span>.
            </DialogDescription>
          </DialogHeader>
          <div className="bg-amber-900/30 p-4 rounded-lg border border-amber-600 my-4">
            <p className="text-amber-300 text-sm">
              <strong>Important:</strong> This will become your new login email for the Client Portal. Make sure you have access to this email address.
            </p>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setShowEmailConfirm(false)}
              className="border-slate-600 text-slate-300"
            >
              Cancel
            </Button>
            <Button
              onClick={handleEmailChange}
              disabled={savingEmail}
              className="bg-teal-600 hover:bg-teal-700"
            >
              {savingEmail ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Updating...
                </>
              ) : (
                'Confirm Change'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Add Student to Class Dialog */}
      <Dialog open={!!addStudentBooking} onOpenChange={(open) => !open && setAddStudentBooking(null)}>
        <DialogContent className="bg-slate-800 border-slate-700 text-white max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-teal-400">
              <Plus className="w-5 h-5" />
              Add Student to Class
            </DialogTitle>
            <DialogDescription className="text-slate-400">
              {addStudentBooking && (
                <>
                  <span className="font-medium text-white">{addStudentBooking.class_type_name}</span>
                  <br />
                  {formatDate(addStudentBooking.start_time)} at {formatTime(addStudentBooking.start_time)}
                  {addStudentBooking.location_name && (
                    <>
                      <br />
                      <span className="flex items-center gap-1 mt-1">
                        <MapPin className="w-3 h-3" />
                        {addStudentBooking.location_name}
                      </span>
                    </>
                  )}
                </>
              )}
            </DialogDescription>
          </DialogHeader>
          
          {/* Approval notice */}
          <div className="p-3 bg-amber-900/30 border border-amber-600/50 rounded-lg">
            <div className="flex items-start gap-2">
              <AlertTriangle className="w-4 h-4 text-amber-400 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-amber-300">
                Your class is subject to approval before additional students can be added.
              </p>
            </div>
          </div>
          
          {/* Show result if we have one */}
          {addStudentResult && (
            <div className={`p-4 rounded-lg border ${
              addStudentResult.success 
                ? 'bg-green-900/30 border-green-600/50' 
                : 'bg-red-900/30 border-red-600/50'
            }`}>
              <div className="flex items-start gap-2">
                {addStudentResult.success ? (
                  <CheckCircle className="w-5 h-5 text-green-400 flex-shrink-0 mt-0.5" />
                ) : (
                  <AlertCircle className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
                )}
                <div>
                  <p className={addStudentResult.success ? 'text-green-300' : 'text-red-300'}>
                    {addStudentResult.message}
                  </p>
                  {addStudentResult.scenario === 'invoice_updated' && (
                    <p className="text-sm text-slate-400 mt-1">
                      New total: ${addStudentResult.new_total?.toFixed(2)} | Due: ${addStudentResult.amount_due?.toFixed(2)}
                    </p>
                  )}
                  {addStudentResult.scenario === 'invoice_paid' && addStudentResult.reference_code && (
                    <div className="mt-2">
                      <p className="text-sm text-slate-400 mb-2">
                        Use this booking code to add a student with payment:
                      </p>
                      <div className="flex items-center gap-2">
                        <span className="font-mono text-teal-400 bg-slate-700 px-2 py-1 rounded">
                          {addStudentResult.reference_code}
                        </span>
                        <a
                          href={addStudentResult.booking_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-xs text-teal-400 hover:text-teal-300 flex items-center gap-1"
                        >
                          Go to Booking
                          <ExternalLink className="w-3 h-3" />
                        </a>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
          
          {/* Show form if no result yet or result was unsuccessful */}
          {(!addStudentResult || !addStudentResult.success) && addStudentResult?.scenario !== 'within_24_hours' && addStudentResult?.scenario !== 'invoice_paid' && (
            <div className="space-y-4">
              {/* Scenario message */}
              {addStudentBooking && (
                <div className="p-3 bg-slate-700/50 rounded-lg text-sm text-slate-300">
                  {getAddStudentScenario(addStudentBooking)?.message}
                </div>
              )}
              
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2">
                  <Label className="text-slate-300">First Name *</Label>
                  <Input
                    value={addStudentForm.first_name}
                    onChange={(e) => setAddStudentForm(prev => ({ ...prev, first_name: e.target.value }))}
                    placeholder="First name"
                    className="bg-slate-700 border-slate-600 text-white"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-slate-300">Last Name *</Label>
                  <Input
                    value={addStudentForm.last_name}
                    onChange={(e) => setAddStudentForm(prev => ({ ...prev, last_name: e.target.value }))}
                    placeholder="Last name"
                    className="bg-slate-700 border-slate-600 text-white"
                  />
                </div>
              </div>
              
              <div className="space-y-2">
                <Label className="text-slate-300">Email *</Label>
                <Input
                  type="email"
                  value={addStudentForm.email}
                  onChange={(e) => setAddStudentForm(prev => ({ ...prev, email: e.target.value }))}
                  placeholder="student@email.com"
                  className="bg-slate-700 border-slate-600 text-white"
                />
              </div>
              
              <div className="space-y-2">
                <Label className="text-slate-300">Phone *</Label>
                <Input
                  type="tel"
                  value={addStudentForm.phone}
                  onChange={(e) => setAddStudentForm(prev => ({ ...prev, phone: formatPhoneNumber(e.target.value) }))}
                  placeholder="(555) 123-4567"
                  className="bg-slate-700 border-slate-600 text-white"
                />
              </div>

              {/* AHA eCard Verification for BLS Renewal Classes */}
              {addStudentBooking && isRenewalClass(addStudentBooking) && (
                <div className="space-y-3 p-4 bg-amber-900/20 border border-amber-600/40 rounded-lg">
                  <div className="flex items-center gap-2 text-amber-400">
                    <AlertTriangle className="w-4 h-4" />
                    <span className="font-medium text-sm">BLS Renewal Verification Required</span>
                  </div>
                  <p className="text-xs text-amber-300/80">
                    Each student must provide their AHA eCard code for verification before being added to this renewal class.
                  </p>
                  
                  <div className="space-y-2">
                    <Label className="text-slate-300">AHA eCard Code *</Label>
                    <div className="flex gap-2">
                      <Input
                        value={addStudentForm.aha_ecard_code}
                        onChange={(e) => {
                          setAddStudentForm(prev => ({ ...prev, aha_ecard_code: e.target.value }));
                          // Reset verification when code changes
                          if (ahaVerificationStatus) {
                            setAhaVerificationStatus(null);
                            setManualVerifyConfirmed(false);
                          }
                        }}
                        placeholder="Enter eCard code (e.g., 225511223344)"
                        className="bg-slate-700 border-slate-600 text-white flex-1"
                      />
                      <Button
                        type="button"
                        onClick={verifyAhaEcard}
                        disabled={verifyingAha || !addStudentForm.first_name?.trim() || !addStudentForm.last_name?.trim() || !addStudentForm.aha_ecard_code?.trim()}
                        className="bg-amber-600 hover:bg-amber-700 text-white"
                      >
                        {verifyingAha ? (
                          <>
                            <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                            Verifying...
                          </>
                        ) : (
                          'Verify'
                        )}
                      </Button>
                    </div>
                  </div>

                  {/* Verification Result */}
                  {ahaVerificationStatus && (
                    <div className={`p-3 rounded-lg border ${
                      ahaVerificationStatus.match_status === 'AHA MATCH' || ahaVerificationStatus.match_status === 'MATCH FOUND'
                        ? 'bg-green-900/30 border-green-600/50'
                        : ahaVerificationStatus.match_status === 'MANUAL VERIFICATION REQUIRED' || ahaVerificationStatus.match_status === 'AHA UNCLAIMED'
                        ? 'bg-yellow-900/30 border-yellow-600/50'
                        : 'bg-red-900/30 border-red-600/50'
                    }`}>
                      <div className="flex items-start gap-2">
                        {ahaVerificationStatus.match_status === 'AHA MATCH' || ahaVerificationStatus.match_status === 'MATCH FOUND' ? (
                          <CheckCircle className="w-5 h-5 text-green-400 flex-shrink-0" />
                        ) : ahaVerificationStatus.match_status === 'MANUAL VERIFICATION REQUIRED' || ahaVerificationStatus.match_status === 'AHA UNCLAIMED' ? (
                          <AlertTriangle className="w-5 h-5 text-yellow-400 flex-shrink-0" />
                        ) : (
                          <AlertCircle className="w-5 h-5 text-red-400 flex-shrink-0" />
                        )}
                        <div className="flex-1">
                          <p className={`font-medium ${
                            ahaVerificationStatus.match_status === 'AHA MATCH' || ahaVerificationStatus.match_status === 'MATCH FOUND'
                              ? 'text-green-400'
                              : ahaVerificationStatus.match_status === 'MANUAL VERIFICATION REQUIRED' || ahaVerificationStatus.match_status === 'AHA UNCLAIMED'
                              ? 'text-yellow-400'
                              : 'text-red-400'
                          }`}>
                            {ahaVerificationStatus.match_status === 'AHA MATCH' || ahaVerificationStatus.match_status === 'MATCH FOUND'
                              ? '✓ Verified' 
                              : ahaVerificationStatus.match_status === 'MANUAL VERIFICATION REQUIRED' || ahaVerificationStatus.match_status === 'AHA UNCLAIMED'
                              ? '⚠ Manual Verification Required'
                              : '✗ Verification Failed'}
                          </p>
                          {(ahaVerificationStatus.found_name || ahaVerificationStatus.card_holder_name || ahaVerificationStatus.aha_returned_name) && (
                            <p className="text-sm text-slate-300 mt-1">
                              Name on eCard: {ahaVerificationStatus.found_name || ahaVerificationStatus.card_holder_name || ahaVerificationStatus.aha_returned_name}
                            </p>
                          )}
                          {ahaVerificationStatus.expiration_date && (
                            <p className="text-sm text-slate-300">
                              Expires: {ahaVerificationStatus.expiration_date}
                            </p>
                          )}
                          {ahaVerificationStatus.error && (
                            <p className="text-sm text-red-300 mt-1">{ahaVerificationStatus.error}</p>
                          )}
                          
                          {/* Manual verification checkbox */}
                          {(ahaVerificationStatus.match_status === 'MANUAL VERIFICATION REQUIRED' || ahaVerificationStatus.match_status === 'AHA UNCLAIMED') && (
                            <label className="flex items-center gap-2 mt-3 cursor-pointer">
                              <input
                                type="checkbox"
                                checked={manualVerifyConfirmed}
                                onChange={(e) => setManualVerifyConfirmed(e.target.checked)}
                                className="w-4 h-4 rounded border-slate-500 bg-slate-700 text-amber-500 focus:ring-amber-500"
                              />
                              <span className="text-sm text-yellow-300">
                                I confirm this eCard is valid and matches the student
                              </span>
                            </label>
                          )}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
          
          <DialogFooter className="gap-2">
            <Button
              variant="outline"
              onClick={() => setAddStudentBooking(null)}
              className="border-slate-600 text-slate-300 hover:bg-slate-700"
            >
              {addStudentResult?.success ? 'Close' : 'Cancel'}
            </Button>
            {(!addStudentResult || (!addStudentResult.success && addStudentResult.scenario !== 'within_24_hours' && addStudentResult.scenario !== 'invoice_paid')) && (
              <Button
                onClick={handleAddStudentToBooking}
                disabled={addingStudent}
                className="bg-teal-600 hover:bg-teal-700 text-white"
              >
                {addingStudent ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Adding...
                  </>
                ) : (
                  <>
                    <Plus className="w-4 h-4 mr-2" />
                    Add Student
                  </>
                )}
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Change Password Dialog */}
      <Dialog open={showChangePassword} onOpenChange={(open) => {
        if (!mustChangePassword) setShowChangePassword(open);
      }}>
        <DialogContent className="bg-slate-800 border-slate-700 text-white max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-teal-400">
              <Key className="w-5 h-5" />
              {mustChangePassword ? 'Password Change Required' : 'Change Password'}
            </DialogTitle>
            <DialogDescription className="text-slate-400">
              {mustChangePassword 
                ? 'You must change your password before continuing.'
                : 'Enter your current password and choose a new one.'}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            {/* Password Requirements */}
            <div className="p-3 bg-slate-700/50 rounded-lg">
              <p className="text-sm text-slate-400 mb-2">Password must contain:</p>
              <ul className="text-xs text-slate-500 space-y-1">
                <li className={newPassword.length >= 8 ? 'text-green-400' : ''}>• At least 8 characters</li>
                <li className={/[A-Z]/.test(newPassword) ? 'text-green-400' : ''}>• One uppercase letter</li>
                <li className={/[a-z]/.test(newPassword) ? 'text-green-400' : ''}>• One lowercase letter</li>
                <li className={/[0-9]/.test(newPassword) ? 'text-green-400' : ''}>• One number</li>
                <li className={/[!@#$%^&*()_+\-=\[\]{}|;:,.<>?]/.test(newPassword) ? 'text-green-400' : ''}>• One special character</li>
              </ul>
            </div>
            
            {!mustChangePassword && (
              <div className="space-y-2">
                <Label className="text-slate-300">Current Password</Label>
                <Input
                  type="password"
                  value={currentPassword}
                  onChange={(e) => setCurrentPassword(e.target.value)}
                  placeholder="Enter current password"
                  className="bg-slate-700 border-slate-600 text-white"
                />
              </div>
            )}
            
            <div className="space-y-2">
              <Label className="text-slate-300">New Password</Label>
              <Input
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                placeholder="Enter new password"
                className="bg-slate-700 border-slate-600 text-white"
              />
            </div>
            
            <div className="space-y-2">
              <Label className="text-slate-300">Confirm New Password</Label>
              <Input
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="Confirm new password"
                className="bg-slate-700 border-slate-600 text-white"
              />
              {confirmPassword && newPassword !== confirmPassword && (
                <p className="text-red-400 text-xs">Passwords do not match</p>
              )}
            </div>
          </div>
          
          <DialogFooter className="gap-2">
            {!mustChangePassword && (
              <Button
                variant="outline"
                onClick={() => {
                  setShowChangePassword(false);
                  setCurrentPassword('');
                  setNewPassword('');
                  setConfirmPassword('');
                }}
                className="border-slate-600 text-slate-300 hover:bg-slate-700"
              >
                Cancel
              </Button>
            )}
            <Button
              onClick={handleChangePassword}
              disabled={changingPassword || !newPassword || newPassword !== confirmPassword}
              className="bg-teal-600 hover:bg-teal-700 text-white"
            >
              {changingPassword ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Changing...
                </>
              ) : (
                'Change Password'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Reset Team Member Password Dialog (Primary only) */}
      <Dialog open={!!resetPasswordContact} onOpenChange={(open) => !open && setResetPasswordContact(null)}>
        <DialogContent className="bg-slate-800 border-slate-700 text-white max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-amber-400">
              <Key className="w-5 h-5" />
              Reset Password
            </DialogTitle>
            <DialogDescription className="text-slate-400">
              Set a temporary password for {resetPasswordContact?.first_name} {resetPasswordContact?.last_name}. 
              They will be required to change it on their next login.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="p-3 bg-slate-700/50 rounded-lg">
              <p className="text-sm text-slate-400 mb-2">Password must contain:</p>
              <ul className="text-xs text-slate-500 space-y-1">
                <li>• At least 8 characters</li>
                <li>• One uppercase letter</li>
                <li>• One lowercase letter</li>
                <li>• One number</li>
                <li>• One special character</li>
              </ul>
            </div>
            
            <div className="space-y-2">
              <Label className="text-slate-300">Temporary Password</Label>
              <Input
                type="text"
                value={resetTempPassword}
                onChange={(e) => setResetTempPassword(e.target.value)}
                placeholder="Enter temporary password"
                className="bg-slate-700 border-slate-600 text-white"
              />
            </div>
          </div>
          
          <DialogFooter className="gap-2">
            <Button
              variant="outline"
              onClick={() => {
                setResetPasswordContact(null);
                setResetTempPassword('');
              }}
              className="border-slate-600 text-slate-300 hover:bg-slate-700"
            >
              Cancel
            </Button>
            <Button
              onClick={handleResetTeamPassword}
              disabled={resettingPassword || !resetTempPassword}
              className="bg-amber-600 hover:bg-amber-700 text-white"
            >
              {resettingPassword ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Resetting...
                </>
              ) : (
                'Reset Password'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Edit Team Member Permissions Dialog (Primary only) */}
      <Dialog open={!!editPermissionsContact} onOpenChange={(open) => !open && setEditPermissionsContact(null)}>
        <DialogContent className="bg-slate-800 border-slate-700 text-white max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-teal-400">
              <Shield className="w-5 h-5" />
              Edit Permissions
            </DialogTitle>
            <DialogDescription className="text-slate-400">
              Set what {editPermissionsContact?.first_name} can access in the portal.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-3">
            {[
              { key: 'classes', label: 'Classes', desc: 'View upcoming and past classes' },
              { key: 'book_class', label: 'Book a Class', desc: 'Request new class bookings' },
              { key: 'class_pricing', label: 'Class Pricing', desc: 'View class pricing' },
              { key: 'invoices', label: 'Invoices', desc: 'View and pay invoices' },
              { key: 'make_payments', label: 'Make Payments', desc: 'Use saved card to pay for classes' },
              { key: 'saved_locations', label: 'Saved Locations', desc: 'View saved locations' },
              { key: 'team_contacts', label: 'Team Contacts', desc: 'View team members' },
            ].map(perm => (
              <div key={perm.key} className="flex items-center justify-between p-3 bg-slate-700/50 rounded-lg">
                <div>
                  <p className="text-white text-sm font-medium">{perm.label}</p>
                  <p className="text-slate-500 text-xs">{perm.desc}</p>
                </div>
                <button
                  type="button"
                  onClick={() => setTempPermissions(prev => ({ ...prev, [perm.key]: !prev[perm.key] }))}
                  className={`w-12 h-6 rounded-full transition-colors ${tempPermissions[perm.key] ? 'bg-teal-600' : 'bg-slate-600'}`}
                >
                  <div className={`w-5 h-5 rounded-full bg-white transition-transform ${tempPermissions[perm.key] ? 'translate-x-6' : 'translate-x-0.5'}`} />
                </button>
              </div>
            ))}
          </div>
          
          <DialogFooter className="gap-2">
            <Button
              variant="outline"
              onClick={() => setEditPermissionsContact(null)}
              className="border-slate-600 text-slate-300 hover:bg-slate-700"
            >
              Cancel
            </Button>
            <Button
              onClick={handleSavePermissions}
              disabled={savingPermissions}
              className="bg-teal-600 hover:bg-teal-700 text-white"
            >
              {savingPermissions ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Saving...
                </>
              ) : (
                'Save Permissions'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Swap Primary Contact Dialog */}
      <Dialog open={showSwapPrimary} onOpenChange={setShowSwapPrimary}>
        <DialogContent className="bg-slate-800 border-slate-700 text-white max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-amber-400">
              <UserCog className="w-5 h-5" />
              Change Primary Contact
            </DialogTitle>
            <DialogDescription className="text-slate-400">
              Select a team member to make them the new primary contact. You will become a team member.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-3">
            {client?.contacts?.map((contact, idx) => (
              <div 
                key={idx}
                onClick={() => setSwapTargetIndex(idx)}
                className={`p-3 rounded-lg cursor-pointer transition-colors ${
                  swapTargetIndex === idx 
                    ? 'bg-teal-600/30 border border-teal-500' 
                    : 'bg-slate-700/50 border border-transparent hover:bg-slate-700'
                }`}
              >
                <p className="text-white font-medium">{contact.first_name} {contact.last_name}</p>
                <p className="text-slate-400 text-sm">{contact.email}</p>
              </div>
            ))}
            
            {(!client?.contacts || client.contacts.length === 0) && (
              <p className="text-slate-400 text-center py-4">No team members available</p>
            )}
          </div>
          
          <DialogFooter className="gap-2">
            <Button
              variant="outline"
              onClick={() => {
                setShowSwapPrimary(false);
                setSwapTargetIndex(null);
              }}
              className="border-slate-600 text-slate-300 hover:bg-slate-700"
            >
              Cancel
            </Button>
            <Button
              onClick={handleSwapPrimary}
              disabled={swappingPrimary || swapTargetIndex === null}
              className="bg-amber-600 hover:bg-amber-700 text-white"
            >
              {swappingPrimary ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Changing...
                </>
              ) : (
                'Make Primary'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Stripe Card Fields Dialog for Saving Payment Method */}
      <Dialog open={showCardFieldsDialog} onOpenChange={(open) => {
        if (!open) {
          setShowCardFieldsDialog(false);
          setSetupTokenId(null);
        }
      }}>
        <DialogContent className="bg-slate-800 border-slate-700 sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <CreditCard className="w-5 h-5 text-teal-400" />
              Add Payment Method
            </DialogTitle>
            <DialogDescription className="text-slate-400">
              Enter your card details below. Your card will be securely saved for future payments.
            </DialogDescription>
          </DialogHeader>
          
          <div className="py-4">
            <StripeCardVaultForm
              clientId={client?.id}
              onSuccess={async (result) => {
                // Close dialog and refresh client data
                setShowCardFieldsDialog(false);
                setSetupTokenId(null);
                toast.success(`Card ending in ${result.cardLastFour} saved successfully!`);
                // Refresh client data to show new card
                if (client?.id) {
                  await fetchClientData(client.id, email);
                }
              }}
              onError={(error) => {
                console.error('Card vault error:', error);
                // Error toast is handled by the component
              }}
              onCancel={() => {
                setShowCardFieldsDialog(false);
                setSetupTokenId(null);
              }}
            />
          </div>
        </DialogContent>
      </Dialog>

      {/* AHA Name Mismatch Warning Modal */}
      <Dialog open={ahaMismatchModal.show} onOpenChange={(open) => !open && handleAhaMismatchReject()}>
        <DialogContent className="sm:max-w-md bg-amber-950 border-2 border-amber-500 shadow-2xl shadow-amber-500/20">
          <DialogHeader className="text-center">
            <div className="mx-auto mb-3 w-16 h-16 rounded-full bg-amber-500/20 flex items-center justify-center">
              <AlertTriangle className="h-10 w-10 text-amber-400" />
            </div>
            <DialogTitle className="text-xl font-bold text-amber-300 text-center">
              ⚠️ Name Mismatch Warning
            </DialogTitle>
            <DialogDescription className="text-slate-300 text-center pt-2">
              The name you entered does not exactly match the AHA records.
            </DialogDescription>
          </DialogHeader>
          
          <div className="py-4 space-y-4">
            <div className="bg-slate-800/50 rounded-lg p-4 space-y-3">
              <div className="flex justify-between items-center">
                <span className="text-slate-400 text-sm">You entered:</span>
                <span className="text-white font-semibold">{ahaMismatchModal.enteredName}</span>
              </div>
              <div className="border-t border-slate-600/50"></div>
              <div className="flex justify-between items-center">
                <span className="text-slate-400 text-sm">AHA shows:</span>
                <span className="text-amber-300 font-semibold">{ahaMismatchModal.ahaName}</span>
              </div>
            </div>
            
            <p className="text-sm text-slate-400 text-center">
              You can accept this student with the name difference (will be marked for review), 
              or go back to correct the name.
            </p>
          </div>
          
          <DialogFooter className="flex gap-3 sm:justify-center">
            <Button
              variant="outline"
              onClick={handleAhaMismatchReject}
              className="flex-1 border-slate-500 text-slate-300 hover:bg-slate-700 hover:text-white"
            >
              Change Name
            </Button>
            <Button
              onClick={handleAhaMismatchAccept}
              className="flex-1 bg-amber-600 hover:bg-amber-500 text-white font-semibold"
            >
              Accept Anyway
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ClientPortal;
